<template>
  <div class="book-publico">
    <header class="book-topbar">
      <div>
        <p class="eyebrow">Book público de projetos</p>
        <h1>Visualização executiva do PDM</h1>
        <p>
          Painéis resumidos, exportáveis e prontos para apresentação a partir dos
          dados do painel público de projetos.
        </p>
      </div>
      <div class="book-actions no-export">
        <button type="button" class="btn secondary" @click="carregarTudo" :disabled="carregando || exportando">
          {{ carregando ? "Atualizando..." : "Atualizar dados" }}
        </button>
        <button type="button" class="btn" @click="exportarApresentacaoPdf" :disabled="!temDados || exportando">
          PDF completo
        </button>
        <button type="button" class="btn ghost" @click="exportarDadosXls" :disabled="!temDados || exportando">
          XLS dados
        </button>
      </div>
    </header>

    <section class="book-filtros">
      <label>
        <span>Município com PDM cadastrado</span>
        <select v-model.number="filtros.idMunicipio" @change="selecionarMunicipio">
          <option :value="null" disabled>Selecione</option>
          <option v-for="municipio in municipiosComPdm" :key="municipio.id" :value="municipio.id">
            {{ formatarTextoRelatorio(municipio.nome, "titulo") }}
          </option>
        </select>
      </label>
      <label>
        <span>PDM</span>
        <select v-model.number="filtros.idPdm" @change="selecionarPdm">
          <option :value="null">Todos do município</option>
          <option v-for="pdm in pdmsDoMunicipio" :key="pdm.idPdm" :value="pdm.idPdm">
            {{ formatarTextoRelatorio(pdm.nome, "titulo") }}
          </option>
        </select>
      </label>
      <label>
        <span>Área-chave</span>
        <select v-model="filtros.areaChave">
          <option value="">Todas as áreas</option>
          <option v-for="area in opcoesFiltroAreas" :key="area.valor" :value="area.valor">
            {{ area.rotulo }}
          </option>
        </select>
      </label>
      <label>
        <span>Prioridade</span>
        <select v-model="filtros.prioridade">
          <option value="">Todas</option>
          <option v-for="prioridade in opcoesFiltroPrioridades" :key="prioridade.valor" :value="prioridade.valor">
            {{ prioridade.rotulo }}
          </option>
        </select>
      </label>
      <label>
        <span>Situação</span>
        <select v-model="filtros.situacao">
          <option value="">Todas</option>
          <option v-for="situacao in opcoesFiltroSituacoes" :key="situacao.valor" :value="situacao.valor">
            {{ situacao.rotulo }}
          </option>
        </select>
      </label>
      <label>
        <span>Saúde</span>
        <select v-model="filtros.saude">
          <option value="">Todas</option>
          <option v-for="saude in opcoesFiltroSaude" :key="saude.valor" :value="saude.valor">
            {{ saude.rotulo }}
          </option>
        </select>
      </label>
      <label>
        <span>Busca</span>
        <input v-model.trim="filtros.busca" type="search" placeholder="Projeto, ação ou KPI" />
      </label>
      <div class="filtro-info">
        <span>{{ pdmsSelecionados.length }} PDM(s)</span>
        <span>{{ projetosComIndicadores.length }} projeto(s) filtrado(s)</span>
        <span>{{ projetosSelecionados.length }} projeto(s) no escopo</span>
        <span v-if="filtrosAtivos">Filtros ativos</span>
        <span>Fonte: Plataforma Cidade Empreendedora</span>
        <button v-if="filtrosAtivos" type="button" class="filtro-limpar" @click="limparFiltrosAvancados">
          Limpar filtros
        </button>
      </div>
    </section>

    <p v-if="erro" class="book-erro">{{ erro }}</p>
    <section v-if="carregando" class="book-loading">Carregando visualização...</section>

    <template v-else-if="temDados">
      <section ref="secaoCapa" class="book-slide capa" :style="estiloCapa">
        <div class="slide-action no-export">
          <button type="button" @click="exportarSecaoPng('capa')">PNG</button>
          <button type="button" @click="exportarSecaoPdf('capa')">PDF</button>
        </div>
        <div class="slide-marca">
          <i class="mdi mdi-city-variant-outline"></i>
          <span>Cidade Empreendedora</span>
        </div>
        <div class="capa-overlay">
          <p class="eyebrow">Plano de Desenvolvimento Municipal (PDM)</p>
          <h2>{{ nomeMunicipioSelecionado }} - {{ siglaEstadoSelecionada }}</h2>
          <strong>Book de resultados preliminares</strong>
          <p>{{ periodoApresentacao }}</p>
        </div>
        <div class="capa-ilustracao">
          <i class="mdi mdi-chart-timeline-variant"></i>
          <i class="mdi mdi-target"></i>
          <i class="mdi mdi-lightbulb-on-outline"></i>
          <i class="mdi mdi-map-marker-path"></i>
        </div>
        <div class="capa-faixa">
          <article v-for="item in capaKpis" :key="item.rotulo">
            <i :class="item.icone"></i>
            <span>{{ item.rotulo }}</span>
            <strong>{{ item.valor }}</strong>
          </article>
        </div>
        <footer class="slide-footer">
          <span>Cidade Empreendedora</span>
          <span>{{ nomeMunicipioSelecionado }} | {{ siglaEstadoSelecionada }}</span>
        </footer>
      </section>

      <section ref="secaoExecutivo" class="book-slide executivo">
        <div class="slide-action no-export">
          <button type="button" @click="exportarSecaoPng('executivo')">PNG</button>
          <button type="button" @click="exportarSecaoPdf('executivo')">PDF</button>
        </div>
        <div class="slide-side-label">02</div>
        <div class="slide-head">
          <div>
            <span>{{ nomeMunicipioSelecionado }} - {{ siglaEstadoSelecionada }}</span>
            <h2>Resumo executivo</h2>
          </div>
          <p>{{ interpretacaoGeral }}</p>
        </div>
        <div class="executivo-grid">
          <div class="progresso-card">
            <span>Execução média dos projetos</span>
            <div class="anel" :style="estiloAnel(execucaoMedia)">
              <strong>{{ execucaoMedia }}%</strong>
            </div>
            <small>{{ faseExecucao(execucaoMedia) }}</small>
          </div>
          <div class="metricas-grid">
            <article v-for="item in resumoKpis" :key="item.rotulo">
              <i :class="item.icone"></i>
              <span>{{ item.rotulo }}</span>
              <strong>{{ item.valor }}</strong>
              <small>{{ item.detalhe }}</small>
            </article>
          </div>
          <div class="status-box">
            <h3>Status da gestão</h3>
            <div v-for="item in statusResumo" :key="item.rotulo" class="status-row">
              <span>{{ item.rotulo }}</span>
              <div><i :style="{ width: `${item.percentual}%` }"></i></div>
              <strong>{{ item.quantidade }}</strong>
            </div>
          </div>
          <div class="acoes-status-box">
            <h3>Status das ações</h3>
            <div v-for="item in resumoAcoesStatus" :key="item.chave" class="status-row">
              <span>{{ item.rotulo }}</span>
              <div><i :class="item.classe" :style="{ width: `${item.percentual}%` }"></i></div>
              <strong>{{ item.quantidade }}</strong>
            </div>
          </div>
          <div class="saude-box">
            <h3>Leitura cruzada</h3>
            <article v-for="item in leituraCruzada" :key="item.rotulo">
              <span>{{ item.rotulo }}</span>
              <strong>{{ item.valor }}</strong>
              <small>{{ item.detalhe }}</small>
            </article>
          </div>
        </div>
      </section>

      <section ref="secaoAreas" class="book-slide areas">
        <div class="slide-action no-export">
          <button type="button" @click="exportarSecaoPng('areas')">PNG</button>
          <button type="button" @click="exportarSecaoPdf('areas')">PDF</button>
        </div>
        <div class="slide-side-label">03</div>
        <div class="slide-head compacto">
          <div>
            <span>{{ nomeMunicipioSelecionado }} - {{ siglaEstadoSelecionada }}</span>
            <h2>Progresso das áreas-chave</h2>
          </div>
          <p>Visão consolidada das áreas temáticas, com objetivos, resultados-chave, projetos e pontos de atenção.</p>
        </div>
        <div class="area-grid">
          <article
            v-for="area in areasResumo"
            :key="area.chave"
            :ref="`area-${area.chave}`"
            class="area-card"
          >
            <div class="card-export-group no-export">
              <button type="button" @click.stop="exportarRefPng(`area-${area.chave}`, `area-${area.chave}`)">
                PNG
              </button>
              <button type="button" @click.stop="exportarRefPdf(`area-${area.chave}`, `area-${area.chave}`)">
                PDF
              </button>
            </div>
            <div class="area-head">
              <span :style="{ background: area.cor }">
                <i :class="area.icone"></i>
              </span>
              <div>
                <small>Área {{ area.indice }}</small>
                <h3>{{ area.nome }}</h3>
              </div>
            </div>
            <p class="area-descricao">{{ area.descricao }}</p>
            <div class="area-body">
              <div class="mini-anel" :style="estiloAnel(area.execucao)">
                <strong>{{ area.execucao }}%</strong>
              </div>
              <ul>
                <li><b>{{ area.qtdProjetos }}</b> projeto(s)</li>
                <li><b>{{ area.objetivos }}</b> objetivo(s)</li>
                <li><b>{{ area.resultados }}</b> resultado(s)-chave</li>
              </ul>
            </div>
            <div class="area-destaques">
              <p><span>Maior progresso</span>{{ area.maiorProgresso }}</p>
              <p><span>Ponto de atenção</span>{{ area.menorProgresso }}</p>
            </div>
            <div class="area-mini-objetivos">
              <div v-for="objetivo in area.objetivosDetalhes.slice(0, 3)" :key="objetivo.chave">
                <span>{{ objetivo.nome }}</span>
                <strong>{{ objetivo.valor }}%</strong>
                <i :style="{ width: `${objetivo.valor}%`, background: area.cor }"></i>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section
        v-for="area in areasResumo"
        :key="`slide-area-${area.chave}`"
        :ref="`secaoAreaDetalhe-${area.chave}`"
        class="book-slide area-detalhe"
        :style="{ '--area-cor': area.cor }"
      >
        <div class="slide-action no-export">
          <button type="button" @click="exportarRefPng(`secaoAreaDetalhe-${area.chave}`, `area-detalhe-${area.chave}`)">PNG</button>
          <button type="button" @click="exportarRefPdf(`secaoAreaDetalhe-${area.chave}`, `area-detalhe-${area.chave}`)">PDF</button>
        </div>
        <div class="slide-side-label">{{ String(area.indice + 3).padStart(2, "0") }}</div>
        <div class="slide-head area-head-slide">
          <div>
            <span>{{ nomeMunicipioSelecionado }} - {{ siglaEstadoSelecionada }}</span>
            <h2>{{ area.nome }}</h2>
          </div>
          <p>{{ area.leitura }}</p>
        </div>
        <div class="area-detalhe-grid">
          <aside class="area-narrativa">
            <div class="area-icon-large"><i :class="area.icone"></i></div>
            <div class="anel area-ring" :style="estiloAnel(area.execucao)">
              <strong>{{ area.execucao }}%</strong>
            </div>
            <h3>Progresso da área</h3>
            <p>{{ area.qtdProjetos }} projeto(s), {{ area.objetivos }} objetivo(s) e {{ area.resultados }} resultado(s)-chave em acompanhamento.</p>
          </aside>
          <div class="objetivos-painel">
            <div class="objetivo-extremos">
              <article>
                <i class="mdi mdi-trending-up"></i>
                <span>Maior progresso entre objetivos</span>
                <strong>{{ area.maiorProgresso }}</strong>
              </article>
              <article>
                <i class="mdi mdi-alert-circle-outline"></i>
                <span>Menor progresso entre objetivos</span>
                <strong>{{ area.menorProgresso }}</strong>
              </article>
            </div>
            <div class="objetivos-lista">
              <h3>Progressos individuais dos objetivos cadastrados</h3>
              <div v-for="objetivo in area.objetivosDetalhes" :key="objetivo.chave" class="objetivo-row">
                <span>
                  {{ objetivo.nome }}
                  <small v-if="objetivo.resultadosResumo">{{ objetivo.resultadosResumo }}</small>
                </span>
                <div><i :style="{ width: `${objetivo.valor}%` }"></i></div>
                <strong>{{ objetivo.valor }}%</strong>
              </div>
              <p v-if="!area.objetivosDetalhes.length" class="vazio-inline">Sem objetivos cadastrados para esta área.</p>
            </div>
          </div>
        </div>
        <div class="area-resultados">
          <article v-for="resultado in area.resultadosDestaque" :key="resultado.chave">
            <i class="mdi mdi-checkbox-marked-circle-outline"></i>
            <span>{{ resultado.nome }}</span>
            <strong>{{ resultado.valor }}%</strong>
          </article>
        </div>
      </section>

      <section ref="secaoProjetos" class="book-slide projetos">
        <div class="slide-action no-export">
          <button type="button" @click="exportarSecaoPng('projetos')">PNG</button>
          <button type="button" @click="exportarSecaoPdf('projetos')">PDF</button>
        </div>
        <div class="slide-side-label">{{ String(areasResumo.length + 4).padStart(2, "0") }}</div>
        <div class="slide-head compacto">
          <div>
            <span>{{ nomeMunicipioSelecionado }} - {{ siglaEstadoSelecionada }}</span>
            <h2>Projetos prioritários</h2>
          </div>
          <p>Carteira priorizada por prioridade, saúde, prazo e estágio de execução, para leitura rápida em reuniões executivas.</p>
        </div>
        <div class="projetos-prioritarios">
          <article
            v-for="(projeto, index) in projetosPrioritarios"
            :key="projeto.idProjeto"
            :ref="`projeto-${projeto.idProjeto}`"
            class="projeto-card"
            :class="classeProjetoPrioridade(projeto)"
          >
            <div class="card-export-group no-export">
              <button type="button" @click.stop="exportarRefPng(`projeto-${projeto.idProjeto}`, `projeto-${projeto.idProjeto}`)">
                PNG
              </button>
              <button type="button" @click.stop="exportarRefPdf(`projeto-${projeto.idProjeto}`, `projeto-${projeto.idProjeto}`)">
                PDF
              </button>
            </div>
            <div class="projeto-posicao">{{ index + 1 }}</div>
            <div class="projeto-visual">
              <i :class="iconeProjeto(projeto)"></i>
            </div>
            <div class="projeto-info">
              <span>{{ projeto.nomePdm || "PDM" }}</span>
              <h3>{{ projeto.titulo }}</h3>
              <p>{{ resumoProjeto(projeto) }}</p>
            </div>
            <div class="projeto-meta">
              <b><small>Exec.</small>{{ projeto.execucao }}%</b>
              <span class="meta-chip"><small>Situação</small>{{ formatarSituacaoProjeto(projeto.situacao) }}</span>
              <span class="meta-chip" :class="classeSaudeChip(projeto.nivelSaude)"><small>Saúde</small>{{ formatarSaude(projeto.nivelSaude) }}</span>
              <span class="meta-chip" :class="classePrioridadeChip(projeto.prioridade)"><small>Prioridade</small>{{ formatarTextoRelatorio(projeto.prioridade || "Não informada", "frase") }}</span>
              <span>{{ projeto.prazoTexto }}</span>
            </div>
          </article>
        </div>
      </section>

      <section
        v-for="(projeto, index) in projetosParaSlides"
        :key="`slide-projeto-${projeto.idProjeto}`"
        :ref="`secaoProjetoDetalhe-${projeto.idProjeto}`"
        class="book-slide projeto-detalhe"
      >
        <div class="slide-action no-export">
          <button type="button" @click="exportarRefPng(`secaoProjetoDetalhe-${projeto.idProjeto}`, `projeto-detalhe-${projeto.idProjeto}`)">PNG</button>
          <button type="button" @click="exportarRefPdf(`secaoProjetoDetalhe-${projeto.idProjeto}`, `projeto-detalhe-${projeto.idProjeto}`)">PDF</button>
        </div>
        <div class="slide-side-label">{{ String(areasResumo.length + 5 + index).padStart(2, "0") }}</div>
        <div class="slide-head compacto">
          <div>
            <span>{{ projeto.nomePdm || "PDM" }}</span>
            <h2>{{ projeto.titulo }}</h2>
          </div>
          <p>{{ resumoProjeto(projeto) }}</p>
        </div>
        <div class="projeto-detalhe-grid">
          <aside class="projeto-raio">
            <div class="projeto-visual grande">
              <i :class="iconeProjeto(projeto)"></i>
            </div>
            <div class="anel projeto-ring" :style="estiloAnel(projeto.execucao)">
              <strong>{{ projeto.execucao }}%</strong>
            </div>
            <div class="projeto-tags">
              <span><small>Situação</small>{{ formatarSituacaoProjeto(projeto.situacao) }}</span>
              <span :class="classeSaudeChip(projeto.nivelSaude)"><small>Saúde</small>{{ formatarSaude(projeto.nivelSaude) }}</span>
              <span :class="classePrioridadeChip(projeto.prioridade)"><small>Prioridade</small>{{ formatarTextoRelatorio(projeto.prioridade || "Não informada", "frase") }}</span>
            </div>
            <p class="projeto-contexto">{{ contextoProjeto(projeto) }}</p>
            <div class="acoes-mini-resumo">
              <span v-for="item in resumoAcoesProjeto(projeto)" :key="item.chave" :class="item.classe">
                <b>{{ item.quantidade }}</b>{{ item.rotulo }}
              </span>
            </div>
          </aside>
          <div class="projeto-dados">
            <div class="projeto-kpis">
              <article v-for="item in dadosProjetoDetalhe(projeto)" :key="item.rotulo">
                <i :class="item.icone"></i>
                <span>{{ item.rotulo }}</span>
                <strong>{{ item.valor }}</strong>
              </article>
            </div>
            <div v-if="metricasDestaqueProjeto(projeto).length" class="metricas-detalhe">
              <h3>KPIs do projeto</h3>
              <div v-for="metrica in metricasDestaqueProjeto(projeto)" :key="metrica.chave" class="metrica-row">
                <span>{{ metrica.nome }}</span>
                <div><i :style="{ width: `${metrica.valor}%` }"></i></div>
                <strong>{{ metrica.valor }}%</strong>
              </div>
            </div>
            <div class="acoes-projeto">
              <h3>Ações e entregáveis em destaque</h3>
              <div v-for="acao in acoesDestaqueProjeto(projeto)" :key="acao.chave" class="acao-row">
                <span>{{ acao.nome }}</span>
                <em :class="acao.classeStatus">{{ acao.status }}</em>
                <small>{{ acao.prazo }}</small>
                <div><i :style="{ width: `${acao.valor}%` }"></i></div>
                <strong>{{ acao.valor }}%</strong>
              </div>
              <p v-if="!acoesDestaqueProjeto(projeto).length" class="vazio-inline">Sem ações detalhadas cadastradas.</p>
            </div>
          </div>
        </div>
      </section>

      <section ref="secaoCronograma" class="book-slide cronograma">
        <div class="slide-action no-export">
          <button type="button" @click="exportarSecaoPng('cronograma')">PNG</button>
          <button type="button" @click="exportarSecaoPdf('cronograma')">PDF</button>
        </div>
        <div class="slide-side-label">{{ String(areasResumo.length + projetosParaSlides.length + 5).padStart(2, "0") }}</div>
        <div class="slide-head compacto">
          <div>
            <span>{{ nomeMunicipioSelecionado }} - {{ siglaEstadoSelecionada }}</span>
            <h2>Cronograma e entregáveis</h2>
          </div>
          <p>Todos os projetos filtrados com período definido, status operacional das ações e principais entregáveis.</p>
        </div>
        <div class="timeline-resumo">
          <span v-for="item in resumoAcoesStatus" :key="`timeline-${item.chave}`" :class="item.classe">
            <b>{{ item.quantidade }}</b>{{ item.rotulo }}
          </span>
        </div>
        <div class="timeline">
          <article v-for="item in cronogramaProjetos" :key="item.idProjeto">
            <div>
              <span>{{ formatarData(item.inicio) }} - {{ formatarData(item.fim) }}</span>
              <h3>{{ item.titulo }}</h3>
              <p>{{ item.acoesResumo }}</p>
              <small>{{ item.statusAcoesResumo }}</small>
            </div>
            <em :class="item.classeStatus">{{ item.statusOperacional }}</em>
            <strong>{{ item.execucao }}%</strong>
          </article>
        </div>
      </section>
    </template>

    <section v-else class="book-vazio">
      Selecione um município com PDM cadastrado para gerar a apresentação.
    </section>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import cidadeBase from "@/assets/img/mapa/cidade-base.jpg";
import {
  buscarPdms,
  buscarProjetos,
  buscarPdmDetalhe,
  buscarProjetosDetalhesLote,
  buscarUnidadesRegionaisPublicas,
  buscarMunicipiosPublicos,
} from "../services/api";

export default {
  name: "PainelPublicoApresentacaoProjetos",
  data() {
    return {
      carregando: false,
      exportando: false,
      erro: "",
      unidadesRegionais: [],
      municipios: [],
      pdms: [],
      projetos: [],
      pdmDetalhes: {},
      projetoDetalhesCache: {},
      ultimaAtualizacao: new Date(),
      filtros: {
        idMunicipio: null,
        idPdm: null,
        areaChave: "",
        prioridade: "",
        situacao: "",
        saude: "",
        busca: "",
      },
      situacoesGestaoProjetos: ["Ideacao", "Planejamento", "Execucao", "Monitoramento", "Concluido", "EmEspera", "Cancelado"],
    };
  },
  computed: {
    municipiosComPdm() {
      const ids = new Set(
        this.pdms
          .map((pdm) => Number(pdm.idMunicipio))
          .filter((id) => Number.isFinite(id) && id > 0)
      );
      return this.municipios
        .filter((municipio) => ids.has(Number(municipio.id)))
        .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
    },
    municipioSelecionado() {
      return this.municipios.find((municipio) => Number(municipio.id) === Number(this.filtros.idMunicipio)) || null;
    },
    nomeMunicipioSelecionado() {
      return this.formatarTextoRelatorio(this.municipioSelecionado?.nome || "Município", "titulo");
    },
    siglaEstadoSelecionada() {
      const pdm = this.pdmsSelecionados[0] || this.pdmsDoMunicipio[0];
      return pdm?.siglaEstado || this.municipioSelecionado?.siglaEstado || "";
    },
    pdmsDoMunicipio() {
      if (!this.filtros.idMunicipio) return [];
      return this.pdms
        .filter((pdm) => Number(pdm.idMunicipio) === Number(this.filtros.idMunicipio))
        .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR"));
    },
    pdmsSelecionados() {
      if (this.filtros.idPdm) {
        return this.pdmsDoMunicipio.filter((pdm) => Number(pdm.idPdm) === Number(this.filtros.idPdm));
      }
      return this.pdmsDoMunicipio;
    },
    idsProjetosVinculados() {
      const ids = new Set();
      this.pdmsSelecionados.forEach((pdm) => {
        const detalhe = this.pdmDetalhes[pdm.idPdm];
        (detalhe?.eixos || []).forEach((eixo) => {
          (eixo.projetos || []).forEach((vinculo) => {
            const id = Number(vinculo.idProjeto);
            if (Number.isFinite(id) && id > 0) ids.add(id);
          });
        });
      });
      return ids;
    },
    projetosSelecionados() {
      const idsPdm = new Set(this.pdmsSelecionados.map((pdm) => Number(pdm.idPdm)));
      const idsProjetos = this.idsProjetosVinculados;
      return this.projetos
        .filter((projeto) => {
          const idProjeto = Number(projeto.idProjeto);
          const idPdm = Number(projeto.idPdm);
          const mesmoMunicipio = Number(projeto.idMunicipio) === Number(this.filtros.idMunicipio);
          return idsPdm.has(idPdm) || idsProjetos.has(idProjeto) || (mesmoMunicipio && !idPdm && idsPdm.size > 0);
        })
        .sort((a, b) => String(a.titulo || "").localeCompare(String(b.titulo || ""), "pt-BR"));
    },
    filtrosAtivos() {
      return !!(
        this.filtros.areaChave ||
        this.filtros.prioridade ||
        this.filtros.situacao ||
        this.filtros.saude ||
        this.filtros.busca
      );
    },
    opcoesFiltroAreas() {
      return this.areasBaseFiltro.map((area) => ({
        valor: area.chave,
        rotulo: area.nome,
      }));
    },
    areasBaseFiltro() {
      const areas = [];
      this.pdmsSelecionados.forEach((pdm) => {
        const detalhe = this.pdmDetalhes[pdm.idPdm];
        (detalhe?.eixos || []).forEach((eixo) => {
          const idEixo = Number(eixo.idEixo || eixo.id || eixo.projectStrategicAxisId);
          areas.push({
            chave: this.slug(`${pdm.idPdm}-${eixo.nome || idEixo}`),
            idEixo,
            nome: this.formatarTextoRelatorio(eixo.nome || "Área-chave", "titulo"),
          });
        });
      });
      return areas.sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    },
    opcoesFiltroPrioridades() {
      return this.opcoesProjetoCampo("prioridade", (valor) => this.formatarTextoRelatorio(valor || "Não informada", "frase"));
    },
    opcoesFiltroSituacoes() {
      return this.opcoesProjetoCampo("situacao", (valor) => this.formatarSituacaoProjeto(valor));
    },
    opcoesFiltroSaude() {
      return this.opcoesProjetoCampo("nivelSaude", (valor) => this.formatarSaude(valor));
    },
    temDados() {
      return !!this.municipioSelecionado && this.pdmsSelecionados.length > 0;
    },
    projetosComIndicadores() {
      return this.projetosSelecionados.map((projeto) => {
        const execucao = this.execucaoProjeto(projeto);
        return {
          ...projeto,
          titulo: this.formatarTextoRelatorio(projeto.titulo || "Projeto sem titulo", "titulo"),
          execucao,
          nomePdm: this.nomePdmProjeto(projeto),
          prazoTexto: this.prazoProjetoTexto(projeto),
          prioridadePeso: this.pesoPrioridade(projeto.prioridade),
          saudePeso: this.pesoSaude(projeto.nivelSaude),
          prazoPeso: this.projetoComPrazoVencido(projeto) ? 3 : this.projetoAindaNaoIniciadoPorData(projeto) ? 1 : 0,
        };
      }).filter((projeto) => this.projetoPassaFiltros(projeto));
    },
    execucaoMedia() {
      if (!this.projetosComIndicadores.length) return 0;
      return Math.round(
        this.projetosComIndicadores.reduce((acc, projeto) => acc + projeto.execucao, 0) / this.projetosComIndicadores.length
      );
    },
    progressoOkrMedio() {
      const areas = this.areasResumo.filter((area) => area.resultados > 0);
      if (!areas.length) return 0;
      return Math.round(areas.reduce((acc, area) => acc + area.execucaoOkr, 0) / areas.length);
    },
    capaKpis() {
      return [
        { rotulo: "PDMs", valor: this.pdmsSelecionados.length, icone: "mdi mdi-file-document-outline" },
        { rotulo: "Projetos", valor: this.projetosComIndicadores.length, icone: "mdi mdi-briefcase-variant-outline" },
        { rotulo: "Áreas-chave", valor: this.areasResumo.length, icone: "mdi mdi-view-grid-outline" },
        { rotulo: "Execução média", valor: `${this.execucaoMedia}%`, icone: "mdi mdi-chart-donut" },
      ];
    },
    resumoKpis() {
      return [
        { rotulo: "Projetos filtrados", valor: this.projetosComIndicadores.length, detalhe: "carteira no recorte", icone: "mdi mdi-briefcase-check-outline" },
        { rotulo: "Ações", valor: this.totalAcoes, detalhe: "com estágio operacional", icone: "mdi mdi-format-list-checks" },
        { rotulo: "Métricas", valor: this.totalMetricas, detalhe: "KPIs ligados", icone: "mdi mdi-poll" },
        { rotulo: "OKRs", valor: `${this.progressoOkrMedio}%`, detalhe: "média dos resultados-chave", icone: "mdi mdi-target-variant" },
      ];
    },
    totalAcoes() {
      return this.projetosComIndicadores.reduce((acc, projeto) => acc + this.normalizarAcoesProjeto(this.detalheProjeto(projeto)).length, 0);
    },
    totalMetricas() {
      return this.projetosComIndicadores.reduce((acc, projeto) => {
        const detalhe = this.detalheProjeto(projeto);
        return acc + ((detalhe?.metricas || []).length || Number(projeto?.totalMetricas || 0));
      }, 0);
    },
    periodoApresentacao() {
      const datas = this.pdmsSelecionados
        .flatMap((pdm) => [pdm.periodoInicio, pdm.periodoFim])
        .map((data) => this.parseDate(data))
        .filter(Boolean);
      if (!datas.length) return `Atualizado em ${this.formatarData(this.ultimaAtualizacao)}`;
      const inicio = new Date(Math.min(...datas.map((data) => data.getTime())));
      const fim = new Date(Math.max(...datas.map((data) => data.getTime())));
      return `${this.formatarData(inicio)} a ${this.formatarData(fim)} | atualizado em ${this.formatarData(this.ultimaAtualizacao)}`;
    },
    estiloCapa() {
      return {
        backgroundImage: `linear-gradient(105deg, rgba(3, 15, 38, 0.98) 0%, rgba(5, 27, 63, 0.92) 42%, rgba(5, 27, 63, 0.35) 100%), url(${cidadeBase})`,
      };
    },
    interpretacaoGeral() {
      const projetosCriticos = this.projetosComIndicadores.filter((projeto) => this.pesoSaude(projeto.nivelSaude) >= 3).length;
      const vencidos = this.projetosComIndicadores.filter((projeto) => this.projetoComPrazoVencido(projeto)).length;
      if (this.execucaoMedia >= 75) {
        return `O portfólio apresenta execução consolidada, com ${this.execucaoMedia}% de média. Há ${vencidos} projeto(s) com prazo vencido e ${projetosCriticos} em saúde crítica.`;
      }
      if (this.execucaoMedia >= 35) {
        return `O portfólio está em fase intermediária, com ${this.execucaoMedia}% de execução média. A leitura de prioridade, saúde e prazo indica ${vencidos} ponto(s) vencido(s).`;
      }
      return `O portfólio está em fase inicial de execução, com ${this.execucaoMedia}% de média. A atenção deve se concentrar em projetos críticos, atrasos e prioridades altas.`;
    },
    statusResumo() {
      const total = this.projetosComIndicadores.length || 1;
      return this.situacoesGestaoProjetos.map((situacao) => {
        const quantidade = this.projetosComIndicadores.filter((projeto) => this.chaveOpcaoProjeto(projeto.situacao) === this.chaveOpcaoProjeto(situacao)).length;
        return {
          rotulo: this.formatarSituacaoProjeto(situacao),
          quantidade,
          percentual: Math.round((quantidade / total) * 100),
        };
      });
    },
    resumoAcoesStatus() {
      const itens = [
        { chave: "concluida", rotulo: "Concluídas", classe: "status-concluida", quantidade: 0 },
        { chave: "execucao", rotulo: "Em execução", classe: "status-execucao", quantidade: 0 },
        { chave: "planejamento", rotulo: "Planejamento", classe: "status-planejamento", quantidade: 0 },
        { chave: "atrasada", rotulo: "Atrasadas", classe: "status-atrasada", quantidade: 0 },
      ];
      const mapa = itens.reduce((acc, item) => ({ ...acc, [item.chave]: item }), {});
      this.projetosComIndicadores.forEach((projeto) => {
        this.normalizarAcoesProjeto(this.detalheProjeto(projeto)).forEach((acao) => {
          const chave = this.classificarStatusAcao(acao).chave;
          if (mapa[chave]) mapa[chave].quantidade += 1;
        });
      });
      const total = itens.reduce((acc, item) => acc + item.quantidade, 0) || 1;
      return itens.map((item) => ({
        ...item,
        percentual: Math.round((item.quantidade / total) * 100),
      }));
    },
    leituraCruzada() {
      const saudaveis = this.projetosComIndicadores.filter((projeto) => ["saudavel", "emdia"].includes(this.chaveOpcaoProjeto(projeto.nivelSaude))).length;
      const atencao = this.projetosComIndicadores.filter((projeto) => this.chaveOpcaoProjeto(projeto.nivelSaude) === "atencao").length;
      const criticos = this.projetosComIndicadores.filter((projeto) => this.chaveOpcaoProjeto(projeto.nivelSaude) === "critico").length;
      const prioridadeAlta = this.projetosComIndicadores.filter((projeto) => ["alta", "critica"].includes(this.chaveOpcaoProjeto(projeto.prioridade))).length;
      const vencidos = this.projetosComIndicadores.filter((projeto) => this.projetoComPrazoVencido(projeto)).length;
      return [
        { rotulo: "Saúde", valor: `${saudaveis}/${atencao}/${criticos}`, detalhe: "saudável / atenção / crítico" },
        { rotulo: "Prioridade alta ou critica", valor: prioridadeAlta, detalhe: "projetos com maior foco executivo" },
        { rotulo: "Prazo vencido", valor: vencidos, detalhe: "não concluídos ou cancelados" },
      ];
    },
    areasResumo() {
      const areas = [];
      this.pdmsSelecionados.forEach((pdm) => {
        const detalhe = this.pdmDetalhes[pdm.idPdm];
        (detalhe?.eixos || []).forEach((eixo) => {
          const idEixo = Number(eixo.idEixo || eixo.id || eixo.projectStrategicAxisId);
          const chaveArea = this.slug(`${pdm.idPdm}-${eixo.nome || idEixo}`);
          if (this.filtros.areaChave && this.filtros.areaChave !== chaveArea) return;
          const idsVinculados = new Set((eixo.projetos || []).map((vinculo) => Number(vinculo.idProjeto)));
          const projetos = this.projetosComIndicadores.filter((projeto) => {
            if (idsVinculados.has(Number(projeto.idProjeto))) return true;
            return (projeto.idsEixosPdm || []).map(Number).includes(idEixo);
          });
          const objetivos = eixo.objetivosOkr || [];
          const resultados = objetivos.flatMap((objetivo) => objetivo.resultados || []);
          const execucaoProjetos = projetos.length
            ? Math.round(projetos.reduce((acc, projeto) => acc + projeto.execucao, 0) / projetos.length)
            : 0;
          const execucaoOkr = resultados.length
            ? Math.round(resultados.reduce((acc, resultado) => acc + Number(resultado.percentualAtingido || 0), 0) / resultados.length)
            : 0;
          const progressoObjetivos = objetivos.map((objetivo) => ({
            chave: this.chaveObjetivo(objetivo),
            nome: this.formatarTextoRelatorio(this.obterTituloObjetivo(objetivo), "frase"),
            valor: this.progressoObjetivo(objetivo),
            resultados: (objetivo.resultados || []).length,
            resultadosResumo: this.resumirResultadosObjetivo(objetivo),
          }));
          const resultadosDestaque = resultados
            .map((resultado, index) => ({
              chave: `${idEixo || eixo.nome || "resultado"}-${index}`,
              nome: this.obterTituloResultado(resultado),
              valor: this.limitarPercentual(resultado.percentualAtingido),
            }))
            .sort((a, b) => b.valor - a.valor)
            .slice(0, 4);
          const nomeArea = this.formatarTextoRelatorio(eixo.nome || "Area-chave", "titulo");
          areas.push({
            chave: chaveArea,
            nome: nomeArea,
            descricao: this.descricaoArea(eixo),
            icone: this.iconeArea(eixo.nome),
            cor: this.corArea(eixo.nome, areas.length),
            qtdProjetos: projetos.length,
            objetivos: objetivos.length,
            resultados: resultados.length,
            execucao: resultados.length ? execucaoOkr : execucaoProjetos,
            execucaoOkr,
            execucaoProjetos,
            objetivosDetalhes: progressoObjetivos,
            resultadosDestaque,
            projetosPrioritarios: projetos
              .sort((a, b) => (b.prioridadePeso + b.saudePeso + b.prazoPeso) - (a.prioridadePeso + a.saudePeso + a.prazoPeso))
              .slice(0, 3),
            maiorProgresso: this.descreverExtremoObjetivo(progressoObjetivos, "maior"),
            menorProgresso: this.descreverExtremoObjetivo(progressoObjetivos, "menor"),
            leitura: this.leituraArea(nomeArea, resultados.length ? execucaoOkr : execucaoProjetos, progressoObjetivos.length, projetos.length),
          });
        });
      });
      return areas
        .map((area, index) => ({ ...area, indice: index + 1 }))
        .sort((a, b) => a.indice - b.indice);
    },
    projetosPrioritarios() {
      return [...this.projetosComIndicadores]
        .sort((a, b) =>
          (b.prazoPeso + b.saudePeso + b.prioridadePeso) - (a.prazoPeso + a.saudePeso + a.prioridadePeso) ||
          b.execucao - a.execucao ||
          String(a.titulo || "").localeCompare(String(b.titulo || ""), "pt-BR")
        )
        .slice(0, 8);
    },
    projetosParaSlides() {
      return [...this.projetosComIndicadores]
        .sort((a, b) =>
          (b.prioridadePeso + b.saudePeso + b.prazoPeso) - (a.prioridadePeso + a.saudePeso + a.prazoPeso) ||
          String(a.titulo || "").localeCompare(String(b.titulo || ""), "pt-BR")
        );
    },
    cronogramaProjetos() {
      return this.projetosComIndicadores
        .map((projeto) => {
          const acoes = this.normalizarAcoesProjeto(this.detalheProjeto(projeto));
          const resumoStatus = this.contarAcoesPorStatus(acoes);
          const statusDominante = this.statusDominanteAcoes(resumoStatus);
          return {
            ...projeto,
            inicio: this.parseDate(projeto.dataInicio || projeto.periodoInicio || projeto.inicioPrevisto),
            fim: this.parseDate(projeto.dataFim || projeto.periodoFim || projeto.fimPrevisto),
            acoesResumo: acoes.length
              ? acoes.slice(0, 4).map((acao) => this.formatarTextoRelatorio(acao.titulo || acao.nome || acao.descricao, "frase")).join(" | ")
              : "Sem ações cadastradas",
            statusAcoesResumo: this.formatarResumoStatusAcoes(resumoStatus),
            statusOperacional: statusDominante.rotulo,
            classeStatus: statusDominante.classe,
          };
        })
        .filter((projeto) => projeto.inicio && projeto.fim)
        .sort((a, b) => a.inicio - b.inicio);
    },
  },
  watch: {
    "$route.query.idMunicipio"(valor) {
      const id = Number(valor);
      if (Number.isFinite(id) && id !== Number(this.filtros.idMunicipio)) {
        this.filtros.idMunicipio = id;
        this.carregarDadosSelecionados();
      }
    },
  },
  mounted() {
    this.carregarTudo();
  },
  methods: {
    async carregarTudo() {
      this.carregando = true;
      this.erro = "";
      try {
        const [pdmsResp, projetosResp, municipiosResp, unidadesResp] = await Promise.all([
          buscarPdms(),
          buscarProjetos(),
          buscarMunicipiosPublicos(),
          buscarUnidadesRegionaisPublicas(),
        ]);
        this.unidadesRegionais = (unidadesResp.data || []).map((item) => ({
          ...item,
          id: Number(item.id ?? item.id_unidade_regional),
          nome: item.nome || item.nomeRegiao || "",
        }));
        this.municipios = (municipiosResp.data || []).map((item) => ({
          ...item,
          id: Number(item.id ?? item.municipioId),
          nome: item.nome || "",
          idUnidadeRegional: Number(item.idUnidadeRegional ?? item.id_unidade_regional ?? 0),
        }));
        this.pdms = (pdmsResp.data || []).map((pdm) => ({
          ...pdm,
          idPdm: Number(pdm.idPdm ?? pdm.id),
          idMunicipio: Number(pdm.idMunicipio ?? pdm.municipioId),
        }));
        this.projetos = (projetosResp.data || []).map((projeto) => ({
          ...projeto,
          idProjeto: Number(projeto.idProjeto ?? projeto.id),
          idPdm: Number(projeto.idPdm ?? 0),
          idMunicipio: Number(projeto.idMunicipio ?? projeto.municipioId),
          totalAcoes: Number(projeto.totalAcoes || 0),
          percentualExecucao: Number(projeto.percentualExecucao || 0),
        }));
        this.ultimaAtualizacao = new Date();

        const idQuery = Number(this.$route.query.idMunicipio);
        const municipioInicial = this.municipiosComPdm.find((municipio) => Number(municipio.id) === idQuery) || this.municipiosComPdm[0] || null;
        this.filtros.idMunicipio = municipioInicial ? municipioInicial.id : null;
        this.filtros.idPdm = null;
        await this.carregarDadosSelecionados(false);
      } catch (error) {
        this.erro = "Não foi possível carregar os dados para a apresentação.";
      } finally {
        this.carregando = false;
      }
    },
    async selecionarMunicipio() {
      this.filtros.idPdm = null;
      this.limparFiltrosAvancados();
      await this.carregarDadosSelecionados();
    },
    async selecionarPdm() {
      this.limparFiltrosAvancados();
      await this.carregarDadosSelecionados();
    },
    async carregarDadosSelecionados(atualizarRota = true) {
      if (!this.filtros.idMunicipio) return;
      if (atualizarRota) {
        this.atualizarQueryMunicipio();
      }
      const idsPdm = this.pdmsSelecionados.map((pdm) => pdm.idPdm).filter(Boolean);
      await Promise.all(
        idsPdm
          .filter((idPdm) => !this.pdmDetalhes[idPdm])
          .map(async (idPdm) => {
            const { data } = await buscarPdmDetalhe(idPdm);
            this.$set(this.pdmDetalhes, idPdm, data);
          })
      );
      await this.precarregarDetalhesProjetos(this.projetosSelecionados.map((projeto) => projeto.idProjeto));
    },
    atualizarQueryMunicipio() {
      if (String(this.$route.query.idMunicipio || "") === String(this.filtros.idMunicipio || "")) return;
      this.$router.replace({
        name: this.$route.name,
        query: { ...this.$route.query, idMunicipio: this.filtros.idMunicipio || undefined },
      }).catch(() => {});
    },
    async precarregarDetalhesProjetos(idsProjetos = []) {
      const ids = idsProjetos.filter(Boolean);
      const faltantes = ids.filter((id) => !this.projetoDetalhesCache[id]);
      for (let i = 0; i < faltantes.length; i += 30) {
        const lote = faltantes.slice(i, i + 30);
        const { data } = await buscarProjetosDetalhesLote(lote);
        (data || []).forEach((detalhe) => {
          if (detalhe?.idProjeto) {
            this.$set(this.projetoDetalhesCache, detalhe.idProjeto, detalhe);
          }
        });
      }
    },
    detalheProjeto(projeto) {
      if (!projeto?.idProjeto) return projeto || null;
      return this.projetoDetalhesCache[projeto.idProjeto] || projeto;
    },
    limparFiltrosAvancados() {
      this.filtros.areaChave = "";
      this.filtros.prioridade = "";
      this.filtros.situacao = "";
      this.filtros.saude = "";
      this.filtros.busca = "";
    },
    opcoesProjetoCampo(campo, formatador) {
      const mapa = new Map();
      this.projetosSelecionados.forEach((projeto) => {
        const valorOriginal = projeto?.[campo] || "";
        const chave = this.chaveOpcaoProjeto(valorOriginal || "naoinformada");
        if (!chave || mapa.has(chave)) return;
        mapa.set(chave, {
          valor: chave,
          rotulo: formatador(valorOriginal),
        });
      });
      return Array.from(mapa.values()).sort((a, b) => a.rotulo.localeCompare(b.rotulo, "pt-BR"));
    },
    projetoPassaFiltros(projeto) {
      if (this.filtros.areaChave && !this.areaChavesProjeto(projeto).includes(this.filtros.areaChave)) return false;
      if (this.filtros.prioridade && this.chaveOpcaoProjeto(projeto?.prioridade || "naoinformada") !== this.filtros.prioridade) return false;
      if (this.filtros.situacao && this.chaveOpcaoProjeto(projeto?.situacao || "naoinformada") !== this.filtros.situacao) return false;
      if (this.filtros.saude && this.chaveOpcaoProjeto(projeto?.nivelSaude || "naoinformada") !== this.filtros.saude) return false;
      const busca = this.normalizarStatus(this.filtros.busca);
      if (busca) {
        const detalhe = this.detalheProjeto(projeto);
        const texto = [
          projeto?.titulo,
          projeto?.nomePdm,
          projeto?.descricao,
          detalhe?.descricao,
          ...(this.normalizarAcoesProjeto(detalhe).map((acao) => `${acao.titulo || ""} ${acao.nome || ""} ${acao.descricao || ""}`)),
          ...((detalhe?.metricas || []).map((metrica) => `${metrica.nome || ""} ${metrica.descricao || ""}`)),
        ].join(" ");
        if (!this.normalizarStatus(texto).includes(busca)) return false;
      }
      return true;
    },
    areaChavesProjeto(projeto) {
      const idProjeto = Number(projeto?.idProjeto);
      const idsEixosProjeto = (projeto?.idsEixosPdm || []).map(Number);
      const chaves = [];
      this.pdmsSelecionados.forEach((pdm) => {
        const detalhe = this.pdmDetalhes[pdm.idPdm];
        (detalhe?.eixos || []).forEach((eixo) => {
          const idEixo = Number(eixo.idEixo || eixo.id || eixo.projectStrategicAxisId);
          const vinculado = (eixo.projetos || []).some((vinculo) => Number(vinculo.idProjeto) === idProjeto);
          if (vinculado || idsEixosProjeto.includes(idEixo)) {
            chaves.push(this.slug(`${pdm.idPdm}-${eixo.nome || idEixo}`));
          }
        });
      });
      return chaves;
    },
    normalizarNumero(valor) {
      const numero = Number(valor);
      return Number.isFinite(numero) ? numero : 0;
    },
    limitarPercentual(valor) {
      return Math.max(0, Math.min(100, Math.round(this.normalizarNumero(valor))));
    },
    formatarTextoRelatorio(valor, modo = "frase") {
      const texto = String(valor || "").replace(/\s+/g, " ").trim();
      if (!texto) return "";
      const ajustado = this.deveNormalizarCaixa(texto)
        ? modo === "titulo"
          ? this.aplicarCaixaTitulo(texto)
          : this.aplicarCaixaFrase(texto)
        : texto;
      return this.corrigirPontuacaoRelatorio(this.aplicarExcecoesTexto(ajustado));
    },
    deveNormalizarCaixa(texto) {
      const letras = Array.from(texto).filter((char) => char.toLocaleLowerCase("pt-BR") !== char.toLocaleUpperCase("pt-BR"));
      if (!letras.length) return false;
      const maiusculas = letras.filter((char) => char === char.toLocaleUpperCase("pt-BR")).length;
      const palavrasAltas = texto
        .split(/\s+/)
        .filter((palavra) => {
          const limpo = palavra.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ]/g, "");
          return limpo.length > 3 && limpo === limpo.toLocaleUpperCase("pt-BR");
        }).length;
      return maiusculas / letras.length >= 0.68 || palavrasAltas >= 2;
    },
    aplicarCaixaFrase(texto) {
      const base = texto.toLocaleLowerCase("pt-BR");
      let deveCapitalizar = true;
      return Array.from(base).map((char) => {
        const letra = char.toLocaleLowerCase("pt-BR") !== char.toLocaleUpperCase("pt-BR");
        if (!letra) {
          if ([".", "!", "?", ":"].includes(char)) deveCapitalizar = true;
          return char;
        }
        if (deveCapitalizar) {
          deveCapitalizar = false;
          return char.toLocaleUpperCase("pt-BR");
        }
        return char;
      }).join("");
    },
    aplicarCaixaTitulo(texto) {
      const conectivos = new Set(["a", "o", "as", "os", "de", "da", "do", "das", "dos", "e", "em", "no", "na", "nos", "nas", "por", "para", "com", "sem", "ao", "aos"]);
      const base = texto.toLocaleLowerCase("pt-BR");
      let primeiroTermo = true;
      return base.split(/(\s+|-|\/|:)/).map((parte) => {
        const termo = parte.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ]/g, "");
        if (!termo) {
          if (parte === ":" || parte === "-") primeiroTermo = true;
          return parte;
        }
        const deveManterMinusculo = !primeiroTermo && conectivos.has(parte);
        primeiroTermo = false;
        return deveManterMinusculo ? parte : this.capitalizarPrimeiraLetra(parte);
      }).join("");
    },
    capitalizarPrimeiraLetra(texto) {
      let aplicado = false;
      return Array.from(texto).map((char) => {
        const letra = char.toLocaleLowerCase("pt-BR") !== char.toLocaleUpperCase("pt-BR");
        if (!aplicado && letra) {
          aplicado = true;
          return char.toLocaleUpperCase("pt-BR");
        }
        return char;
      }).join("");
    },
    aplicarExcecoesTexto(texto) {
      let saida = texto
        .replace(/\bpdm\b/gi, "PDM")
        .replace(/\bokrs\b/gi, "OKRs")
        .replace(/\bokr\b/gi, "OKR")
        .replace(/\bmei\b/gi, "MEI")
        .replace(/\bcnpj\b/gi, "CNPJ")
        .replace(/\bcpf\b/gi, "CPF")
        .replace(/\bnrs\b/gi, "NRs")
        .replace(/\bnr\b/gi, "NR")
        .replace(/\bsebrae\b/gi, "Sebrae");

      (this.municipios || []).forEach((municipio) => {
        const nome = String(municipio.nome || "").trim();
        if (!nome) return;
        const nomeFormal = this.aplicarCaixaTitulo(nome);
        saida = saida.replace(new RegExp(this.escaparRegex(nome), "gi"), nomeFormal);
      });
      return saida;
    },
    corrigirPontuacaoRelatorio(texto) {
      let saida = String(texto || "")
        .replace(/\s+([,.;:!?])/g, "$1")
        .replace(/([([{])\s+/g, "$1")
        .replace(/\s+([)\]}])/g, "$1")
        .replace(/\s{2,}/g, " ")
        .trim();
      const aspasDuplas = (saida.match(/"/g) || []).length;
      if (aspasDuplas % 2 === 1) {
        saida = saida.replace(/"/g, "");
      }
      return saida;
    },
    escaparRegex(valor) {
      return String(valor || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    mediaPercentual(valores) {
      const validos = valores.map(Number).filter(Number.isFinite);
      if (!validos.length) return 0;
      return validos.reduce((acc, valor) => acc + valor, 0) / validos.length;
    },
    normalizarAcoesProjeto(detalhe) {
      if (!detalhe) return [];
      const entregasPorMarco = (detalhe.entregas || []).reduce((acc, entrega) => {
        const idMarco = entrega.idMarco ?? entrega.idAcao;
        if (!idMarco) return acc;
        if (!acc[idMarco]) acc[idMarco] = [];
        acc[idMarco].push({
          ...entrega,
          idEntrega: entrega.idEntrega ?? entrega.idAcao ?? null,
          tarefas: entrega.tarefas || [],
          percentualConclusao: this.limitarPercentual(
            entrega.percentualConclusao ?? entrega.progresso ?? this.mediaPercentual((entrega.tarefas || []).map((tarefa) => tarefa.percentualConclusao))
          ),
        });
        return acc;
      }, {});

      const marcos = (detalhe.marcos || []).map((marco) => ({
        ...marco,
        idAcao: marco.idMarco ?? null,
        entregas: entregasPorMarco[marco.idMarco] || [],
        tarefas: (entregasPorMarco[marco.idMarco] || []).flatMap((entrega) => entrega.tarefas || []),
      })).map((marco) => {
        const progressoCalculado = marco.entregas.length
          ? this.mediaPercentual(marco.entregas.map((entrega) => entrega.percentualConclusao))
          : this.mediaPercentual((marco.tarefas || []).map((tarefa) => tarefa.percentualConclusao));
        return {
          ...marco,
          progressoCalculado: this.limitarPercentual(progressoCalculado || marco.progresso || marco.percentualConclusao),
          percentualConclusao: this.limitarPercentual(marco.progresso ?? marco.percentualConclusao ?? progressoCalculado),
        };
      });

      if (marcos.length) return marcos;

      return (detalhe.entregas || []).map((entrega) => ({
        ...entrega,
        idAcao: entrega.idEntrega ?? entrega.idMarco ?? null,
        idEntrega: entrega.idEntrega ?? null,
        entregas: [entrega],
        tarefas: entrega.tarefas || [],
        progressoCalculado: this.limitarPercentual(entrega.percentualConclusao ?? entrega.progresso),
      }));
    },
    classificarStatusAcao(acao) {
      const progresso = this.limitarPercentual(acao?.progressoCalculado ?? acao?.percentualConclusao ?? acao?.progresso);
      const chave = this.chaveOpcaoProjeto(acao?.situacao || acao?.status);
      const limite = this.parseDate(acao?.dataLimite || acao?.dataFim || acao?.fimPrevisto);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      if (limite) limite.setHours(0, 0, 0, 0);

      if (["concluido", "concluida", "finalizado", "finalizada"].includes(chave) || progresso >= 100) {
        return { chave: "concluida", rotulo: "Concluída", classe: "status-concluida" };
      }
      if (limite && limite < hoje) {
        return { chave: "atrasada", rotulo: "Atrasada", classe: "status-atrasada" };
      }
      if (["planejamento", "ideacao", "naoiniciado", "naoiniciada"].includes(chave) || progresso === 0) {
        return { chave: "planejamento", rotulo: "Planejamento", classe: "status-planejamento" };
      }
      return { chave: "execucao", rotulo: "Em execução", classe: "status-execucao" };
    },
    contarAcoesPorStatus(acoes = []) {
      const resumo = {
        concluida: 0,
        execucao: 0,
        planejamento: 0,
        atrasada: 0,
      };
      acoes.forEach((acao) => {
        const status = this.classificarStatusAcao(acao);
        resumo[status.chave] = (resumo[status.chave] || 0) + 1;
      });
      return resumo;
    },
    statusDominanteAcoes(resumo) {
      if (resumo.atrasada > 0) return { chave: "atrasada", rotulo: "Com atraso", classe: "status-atrasada" };
      if (resumo.execucao > 0) return { chave: "execucao", rotulo: "Em execução", classe: "status-execucao" };
      if (resumo.planejamento > 0) return { chave: "planejamento", rotulo: "Planejamento", classe: "status-planejamento" };
      if (resumo.concluida > 0) return { chave: "concluida", rotulo: "Concluído", classe: "status-concluida" };
      return { chave: "planejamento", rotulo: "Sem ações", classe: "status-planejamento" };
    },
    formatarResumoStatusAcoes(resumo) {
      const partes = [
        resumo.concluida ? `${resumo.concluida} concluída(s)` : "",
        resumo.execucao ? `${resumo.execucao} em execução` : "",
        resumo.planejamento ? `${resumo.planejamento} em planejamento` : "",
        resumo.atrasada ? `${resumo.atrasada} atrasada(s)` : "",
      ].filter(Boolean);
      return partes.length ? partes.join(" | ") : "Sem ações classificadas";
    },
    resumoAcoesProjeto(projeto) {
      const resumo = this.contarAcoesPorStatus(this.normalizarAcoesProjeto(this.detalheProjeto(projeto)));
      return [
        { chave: "concluida", rotulo: "concluídas", classe: "status-concluida", quantidade: resumo.concluida },
        { chave: "execucao", rotulo: "em execução", classe: "status-execucao", quantidade: resumo.execucao },
        { chave: "planejamento", rotulo: "planejadas", classe: "status-planejamento", quantidade: resumo.planejamento },
        { chave: "atrasada", rotulo: "atrasadas", classe: "status-atrasada", quantidade: resumo.atrasada },
      ].filter((item) => item.quantidade > 0);
    },
    execucaoProjeto(projeto) {
      const detalhe = this.detalheProjeto(projeto);
      const acoes = this.normalizarAcoesProjeto(detalhe);
      if (acoes.length) {
        return this.limitarPercentual(this.mediaPercentual(acoes.map((acao) => acao.progressoCalculado ?? acao.percentualConclusao)));
      }
      if (Number.isFinite(Number(projeto?.percentualExecucao)) && Number(projeto.percentualExecucao) > 0) {
        return this.limitarPercentual(projeto.percentualExecucao);
      }
      if (projeto?.orcamentoPlanejado) {
        return this.limitarPercentual(((projeto.orcamentoExecutado || 0) / projeto.orcamentoPlanejado) * 100);
      }
      return 0;
    },
    progressoObjetivo(objetivo) {
      const resultados = objetivo?.resultados || [];
      if (!resultados.length) return this.limitarPercentual(objetivo?.progresso || objetivo?.percentualAtingido);
      return this.limitarPercentual(this.mediaPercentual(resultados.map((resultado) => resultado.percentualAtingido)));
    },
    chaveObjetivo(objetivo) {
      return this.slug(objetivo?.idObjetivo || objetivo?.id || this.obterTituloObjetivo(objetivo));
    },
    obterTituloObjetivo(objetivo) {
      return objetivo?.texto || objetivo?.nome || objetivo?.titulo || objetivo?.descricao || "Objetivo sem nome";
    },
    obterTituloResultado(resultado) {
      return this.formatarTextoRelatorio(
        resultado?.texto || resultado?.descricao || resultado?.nome || resultado?.titulo || "Resultado-chave sem nome",
        "frase"
      );
    },
    resumirResultadosObjetivo(objetivo) {
      const resultados = (objetivo?.resultados || [])
        .map((resultado) => `${this.obterTituloResultado(resultado)} (${this.limitarPercentual(resultado.percentualAtingido)}%)`)
        .slice(0, 3);
      if (!resultados.length) return "";
      const complemento = (objetivo.resultados || []).length > resultados.length ? ` +${(objetivo.resultados || []).length - resultados.length}` : "";
      return `KRs: ${resultados.join("; ")}${complemento}`;
    },
    descricaoArea(eixo) {
      const descricao = this.formatarTextoRelatorio(eixo?.descricao || "", "frase");
      if (descricao) return descricao;
      const nome = this.formatarTextoRelatorio(eixo?.nome || "área-chave", "titulo");
      return `${nome} organiza objetivos, resultados-chave e projetos vinculados ao PDM para orientar a leitura estratégica do município.`;
    },
    descreverExtremo(lista, tipo) {
      if (!lista.length) return "Sem dados suficientes";
      const ordenada = [...lista].sort((a, b) => tipo === "maior" ? b.valor - a.valor : a.valor - b.valor);
      const item = ordenada[0];
      return `${item.nome} (${Math.round(item.valor)}%)`;
    },
    descreverExtremoObjetivo(objetivos, tipo) {
      if (!objetivos.length) return "Sem objetivos cadastrados";
      return this.descreverExtremo(objetivos, tipo);
    },
    iconeArea(nome) {
      const chave = this.chaveOpcaoProjeto(nome);
      if (chave.includes("economico") || chave.includes("empreendedor")) return "mdi mdi-storefront-outline";
      if (chave.includes("educacao") || chave.includes("inovacao") || chave.includes("capital")) return "mdi mdi-school-outline";
      if (chave.includes("governanca") || chave.includes("gestao") || chave.includes("social")) return "mdi mdi-account-group-outline";
      if (chave.includes("infraestrutura") || chave.includes("mobilidade") || chave.includes("sustentabilidade")) return "mdi mdi-road-variant";
      return "mdi mdi-map-outline";
    },
    corArea(nome, index = 0) {
      const chave = this.chaveOpcaoProjeto(nome);
      if (chave.includes("economico") || chave.includes("empreendedor")) return "#16a085";
      if (chave.includes("educacao") || chave.includes("inovacao") || chave.includes("capital")) return "#2f6fed";
      if (chave.includes("governanca") || chave.includes("gestao") || chave.includes("social")) return "#f97316";
      if (chave.includes("infraestrutura") || chave.includes("mobilidade") || chave.includes("sustentabilidade")) return "#00a6c8";
      return ["#16a085", "#2f6fed", "#f97316", "#00a6c8"][index % 4];
    },
    leituraArea(nome, execucao, objetivos, projetos) {
      const fase = this.faseExecucao(execucao).toLowerCase();
      return `${nome || "Área"} apresenta ${execucao}% de progresso e está em ${fase}, reunindo ${objetivos} objetivo(s) e ${projetos} projeto(s) vinculados ao PDM.`;
    },
    nomePdmProjeto(projeto) {
      const pdm = this.pdms.find((item) => Number(item.idPdm) === Number(projeto.idPdm));
      if (pdm) return this.formatarTextoRelatorio(pdm.nome, "titulo");
      const idProjeto = Number(projeto.idProjeto);
      const pdmVinculado = this.pdmsSelecionados.find((item) => {
        const detalhe = this.pdmDetalhes[item.idPdm];
        return (detalhe?.eixos || []).some((eixo) => (eixo.projetos || []).some((vinculo) => Number(vinculo.idProjeto) === idProjeto));
      });
      return this.formatarTextoRelatorio(pdmVinculado?.nome || "", "titulo");
    },
    parseDate(valor) {
      if (!valor) return null;
      const data = new Date(valor);
      return Number.isNaN(data.getTime()) ? null : data;
    },
    formatarData(valor) {
      const data = this.parseDate(valor);
      if (!data) return "-";
      return data.toLocaleDateString("pt-BR");
    },
    normalizarStatus(valor) {
      return String(valor || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    chaveOpcaoProjeto(valor) {
      return this.normalizarStatus(valor).replace(/[^a-z0-9]/g, "");
    },
    formatarSituacaoProjeto(valor) {
      const rotulos = {
        ideacao: "Ideação",
        planejamento: "Planejamento",
        execucao: "Execução",
        monitoramento: "Monitoramento",
        concluido: "Concluído",
        emespera: "Em espera",
        cancelado: "Cancelado",
      };
      return rotulos[this.chaveOpcaoProjeto(valor)] || valor || "-";
    },
    formatarSaude(valor) {
      const rotulos = {
        saudavel: "Saudável",
        emdia: "Saudável",
        atencao: "Atenção",
        critico: "Crítico",
      };
      return rotulos[this.chaveOpcaoProjeto(valor)] || "Saúde não informada";
    },
    classePrioridadeChip(valor) {
      const chave = this.chaveOpcaoProjeto(valor);
      if (["alta", "critica"].includes(chave)) return "chip-prioridade-alta";
      if (chave === "media") return "chip-prioridade-media";
      if (chave === "baixa") return "chip-prioridade-baixa";
      return "chip-neutro";
    },
    classeSaudeChip(valor) {
      const chave = this.chaveOpcaoProjeto(valor);
      if (chave === "critico") return "chip-saude-critica";
      if (chave === "atencao") return "chip-saude-atencao";
      if (["saudavel", "emdia"].includes(chave)) return "chip-saude-saudavel";
      return "chip-neutro";
    },
    pesoPrioridade(valor) {
      const pesos = { baixa: 0, media: 1, alta: 2, critica: 3 };
      return pesos[this.chaveOpcaoProjeto(valor)] || 0;
    },
    pesoSaude(valor) {
      const pesos = { saudavel: 0, emdia: 0, atencao: 2, critico: 3 };
      return pesos[this.chaveOpcaoProjeto(valor)] || 1;
    },
    iconeProjeto(projeto) {
      const texto = this.chaveOpcaoProjeto(`${projeto?.titulo || ""} ${projeto?.areaChave || ""}`);
      if (texto.includes("turismo") || texto.includes("cultura")) return "mdi mdi-map-marker-star-outline";
      if (texto.includes("educa") || texto.includes("escola") || texto.includes("talento")) return "mdi mdi-school-outline";
      if (texto.includes("ambiente") || texto.includes("natureza") || texto.includes("sustent")) return "mdi mdi-leaf";
      if (texto.includes("saude") || texto.includes("acolher")) return "mdi mdi-hand-heart-outline";
      if (texto.includes("feira") || texto.includes("comerc") || texto.includes("empreend")) return "mdi mdi-storefront-outline";
      if (this.projetoComPrazoVencido(projeto)) return "mdi mdi-alert-decagram-outline";
      return "mdi mdi-rocket-launch-outline";
    },
    classeProjetoPrioridade(projeto) {
      const score = (projeto?.prioridadePeso || 0) + (projeto?.saudePeso || 0) + (projeto?.prazoPeso || 0);
      if (score >= 7) return "prioridade-critica";
      if (score >= 4) return "prioridade-alta";
      return "prioridade-monitorada";
    },
    resumoProjeto(projeto) {
      const detalhe = this.detalheProjeto(projeto);
      const descricao = this.formatarTextoRelatorio(detalhe?.descricao || projeto?.descricao || "", "frase");
      if (descricao) return this.truncarTexto(descricao, 175);
      const acoes = this.normalizarAcoesProjeto(detalhe).length;
      const metricas = (detalhe?.metricas || []).length || Number(projeto?.totalMetricas || 0);
      const partes = [
        `${acoes} ação(ões)`,
        `${metricas} KPI(s)`,
        `prazo: ${projeto?.prazoTexto || this.prazoProjetoTexto(projeto)}`,
      ];
      return partes.join(" | ");
    },
    contextoProjeto(projeto) {
      const detalhe = this.detalheProjeto(projeto);
      const descricao = this.formatarTextoRelatorio(detalhe?.descricao || detalhe?.objetivos || detalhe?.escopo || "", "frase");
      if (descricao) return this.truncarTexto(descricao, 230);
      return `${this.formatarTextoRelatorio(projeto?.titulo || "Projeto", "titulo")} está em ${this.faseExecucao(projeto?.execucao || 0).toLowerCase()}, com prioridade ${this.formatarTextoRelatorio(projeto?.prioridade || "não informada", "frase").toLowerCase()} e prazo ${projeto?.prazoTexto || this.prazoProjetoTexto(projeto)}.`;
    },
    dadosProjetoDetalhe(projeto) {
      const detalhe = this.detalheProjeto(projeto);
      const resumo = this.contarAcoesPorStatus(this.normalizarAcoesProjeto(detalhe));
      return [
        { rotulo: "Período", valor: this.periodoProjeto(projeto), icone: "mdi mdi-calendar-range" },
        { rotulo: "Ações", valor: this.formatarResumoStatusAcoes(resumo), icone: "mdi mdi-format-list-checks" },
        { rotulo: "Métricas", valor: (detalhe?.metricas || []).length || Number(projeto?.totalMetricas || 0), icone: "mdi mdi-poll" },
        { rotulo: "PDM", valor: this.formatarTextoRelatorio(projeto?.nomePdm || "-", "titulo"), icone: "mdi mdi-file-document-outline" },
      ];
    },
    periodoProjeto(projeto) {
      const inicio = this.formatarData(projeto?.dataInicio || projeto?.periodoInicio || projeto?.inicioPrevisto);
      const fim = this.formatarData(projeto?.dataFim || projeto?.periodoFim || projeto?.fimPrevisto);
      return `${inicio} a ${fim}`;
    },
    acoesDestaqueProjeto(projeto) {
      const acoes = this.normalizarAcoesProjeto(this.detalheProjeto(projeto));
      return acoes
        .map((acao, index) => {
          const status = this.classificarStatusAcao(acao);
          return {
            chave: `${projeto?.idProjeto || "projeto"}-${acao.idAcao || index}`,
            nome: this.formatarTextoRelatorio(acao.titulo || acao.nome || acao.descricao || `Ação ${index + 1}`, "frase"),
            valor: this.limitarPercentual(acao.progressoCalculado ?? acao.percentualConclusao),
            status: status.rotulo,
            classeStatus: status.classe,
            prazo: this.formatarData(acao.dataLimite || acao.dataFim || acao.fimPrevisto),
          };
        })
        .sort((a, b) => b.valor - a.valor)
        .slice(0, 6);
    },
    metricasDestaqueProjeto(projeto) {
      const detalhe = this.detalheProjeto(projeto);
      return (detalhe?.metricas || [])
        .map((metrica, index) => ({
          chave: `${projeto?.idProjeto || "projeto"}-metrica-${metrica.idMetrica || index}`,
          nome: this.formatarTextoRelatorio(metrica.nome || metrica.descricao || `KPI ${index + 1}`, "frase"),
          valor: this.progressoMetrica(metrica),
        }))
        .slice(0, 4);
    },
    progressoMetrica(metrica) {
      const meta = Number(metrica?.meta);
      const atual = Number(metrica?.valorAtual);
      if (Number.isFinite(meta) && meta > 0 && Number.isFinite(atual)) {
        return this.limitarPercentual((atual / meta) * 100);
      }
      return this.limitarPercentual(metrica?.percentualAtingido || metrica?.progresso || 0);
    },
    truncarTexto(texto, limite = 160) {
      const valor = String(texto || "").trim();
      if (valor.length <= limite) return valor;
      return `${valor.slice(0, limite - 1).trim()}…`;
    },
    projetoComPrazoVencido(projeto) {
      const fim = this.parseDate(projeto?.dataFim || projeto?.periodoFim || projeto?.fimPrevisto);
      if (!fim) return false;
      const status = this.chaveOpcaoProjeto(projeto?.situacao || projeto?.status);
      if (["concluido", "cancelado"].includes(status)) return false;
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      fim.setHours(0, 0, 0, 0);
      return fim < hoje;
    },
    projetoAindaNaoIniciadoPorData(projeto) {
      const inicio = this.parseDate(projeto?.dataInicio || projeto?.periodoInicio || projeto?.inicioPrevisto);
      if (!inicio) return false;
      const status = this.chaveOpcaoProjeto(projeto?.situacao || projeto?.status);
      if (["concluido", "cancelado"].includes(status)) return false;
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      inicio.setHours(0, 0, 0, 0);
      return inicio > hoje;
    },
    prazoProjetoTexto(projeto) {
      if (this.projetoComPrazoVencido(projeto)) return "Prazo vencido";
      if (this.projetoAindaNaoIniciadoPorData(projeto)) return "Ainda não iniciado pela data";
      const inicio = this.formatarData(projeto.dataInicio || projeto.periodoInicio || projeto.inicioPrevisto);
      const fim = this.formatarData(projeto.dataFim || projeto.periodoFim || projeto.fimPrevisto);
      return `${inicio} - ${fim}`;
    },
    faseExecucao(valor) {
      if (valor >= 76) return "Execução consolidada";
      if (valor >= 51) return "Execução avançada";
      if (valor >= 26) return "Execução intermediária";
      if (valor > 0) return "Fase inicial de execução";
      return "Sem execução registrada";
    },
    estiloAnel(valor) {
      const percentual = this.limitarPercentual(valor);
      return {
        background: `conic-gradient(#12dce8 ${percentual * 3.6}deg, #d6dce5 0deg)`,
      };
    },
    slug(valor) {
      return this.chaveOpcaoProjeto(valor) || "item";
    },
    nomeArquivo(prefixo, extensao = "") {
      const base = `${prefixo}-${this.slug(this.nomeMunicipioSelecionado)}-${new Date().toISOString().slice(0, 10)}`;
      return extensao ? `${base}.${extensao}` : base;
    },
    obterElementoSecao(chave) {
      const mapa = {
        capa: this.$refs.secaoCapa,
        executivo: this.$refs.secaoExecutivo,
        areas: this.$refs.secaoAreas,
        projetos: this.$refs.secaoProjetos,
        cronograma: this.$refs.secaoCronograma,
      };
      return mapa[chave] || null;
    },
    obterRef(refName) {
      const ref = this.$refs[refName];
      return Array.isArray(ref) ? ref[0] : ref;
    },
    async exportarSecaoPng(chave) {
      const elemento = this.obterElementoSecao(chave);
      if (!elemento) return;
      try {
        const canvas = await this.renderizarElementoCanvas(elemento);
        this.baixarCanvas(canvas, this.nomeArquivo(`painel-${chave}`, "png"));
      } catch (error) {
        this.erro = "Não foi possível exportar este painel.";
      } finally {
        this.exportando = false;
      }
    },
    async exportarSecaoPdf(chave) {
      const elemento = this.obterElementoSecao(chave);
      if (!elemento) return;
      try {
        const canvas = await this.renderizarElementoCanvas(elemento);
        this.baixarPdfCanvas(canvas, this.nomeArquivo(`painel-${chave}`, "pdf"));
      } catch (error) {
        this.erro = "Não foi possível exportar este painel.";
      } finally {
        this.exportando = false;
      }
    },
    async exportarRefPng(refName, nome) {
      const elemento = this.obterRef(refName);
      if (!elemento) return;
      try {
        const canvas = await this.renderizarElementoCanvas(elemento);
        this.baixarCanvas(canvas, this.nomeArquivo(nome, "png"));
      } catch (error) {
        this.erro = "Não foi possível exportar este elemento.";
      } finally {
        this.exportando = false;
      }
    },
    async exportarRefPdf(refName, nome) {
      const elemento = this.obterRef(refName);
      if (!elemento) return;
      try {
        const canvas = await this.renderizarElementoCanvas(elemento);
        this.baixarPdfCanvas(canvas, this.nomeArquivo(nome, "pdf"));
      } catch (error) {
        this.erro = "Não foi possível exportar este elemento.";
      } finally {
        this.exportando = false;
      }
    },
    async exportarApresentacaoPdf() {
      const secoesFixasInicio = ["capa", "executivo", "areas"]
        .map((chave) => this.obterElementoSecao(chave))
        .filter(Boolean);
      const secoesAreas = this.areasResumo
        .map((area) => this.obterRef(`secaoAreaDetalhe-${area.chave}`))
        .filter(Boolean);
      const secoesProjetos = this.projetosParaSlides
        .map((projeto) => this.obterRef(`secaoProjetoDetalhe-${projeto.idProjeto}`))
        .filter(Boolean);
      const secoesFixasFim = ["projetos", "cronograma"]
        .map((chave) => this.obterElementoSecao(chave))
        .filter(Boolean);
      const secoes = [...secoesFixasInicio, ...secoesAreas, ...secoesFixasFim.slice(0, 1), ...secoesProjetos, ...secoesFixasFim.slice(1)];
      if (!secoes.length) return;
      this.exportando = true;
      try {
        const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        for (let i = 0; i < secoes.length; i += 1) {
          const canvas = await this.renderizarElementoCanvas(secoes[i]);
          if (i > 0) pdf.addPage("a4", "landscape");
          this.adicionarCanvasAoPdf(pdf, canvas);
        }
        pdf.save(this.nomeArquivo("book-pdm-apresentacao", "pdf"));
      } finally {
        this.exportando = false;
      }
    },
    exportarDadosXls() {
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([{
        Municipio: `${this.nomeMunicipioSelecionado} - ${this.siglaEstadoSelecionada}`,
        PDMs: this.pdmsSelecionados.map((pdm) => pdm.nome).join(" | "),
        Area: this.opcoesFiltroAreas.find((area) => area.valor === this.filtros.areaChave)?.rotulo || "Todas",
        Prioridade: this.opcoesFiltroPrioridades.find((item) => item.valor === this.filtros.prioridade)?.rotulo || "Todas",
        Situacao: this.opcoesFiltroSituacoes.find((item) => item.valor === this.filtros.situacao)?.rotulo || "Todas",
        Saude: this.opcoesFiltroSaude.find((item) => item.valor === this.filtros.saude)?.rotulo || "Todas",
        Busca: this.filtros.busca || "",
      }]), "Filtros");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.resumoKpis.map((item) => ({
        Indicador: item.rotulo,
        Valor: item.valor,
        Detalhe: item.detalhe,
      }))), "Resumo");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.statusResumo.map((item) => ({
        Status: item.rotulo,
        Quantidade: item.quantidade,
        Percentual: item.percentual,
      }))), "Status");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.resumoAcoesStatus.map((item) => ({
        Estagio: item.rotulo,
        Quantidade: item.quantidade,
        Percentual: item.percentual,
      }))), "Status Acoes");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.areasResumo.map((area) => ({
        Area: area.nome,
        Descricao: area.descricao,
        Execução: area.execucao,
        Projetos: area.qtdProjetos,
        Objetivos: area.objetivos,
        ResultadosChave: area.resultados,
        MaiorProgresso: area.maiorProgresso,
        PontoAtencao: area.menorProgresso,
      }))), "Areas");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.areasResumo.flatMap((area) =>
        area.objetivosDetalhes.map((objetivo) => ({
          Area: area.nome,
          Objetivo: objetivo.nome,
          Progresso: objetivo.valor,
          ResultadosChave: objetivo.resultados,
          KRs: objetivo.resultadosResumo,
        }))
      )), "Objetivos");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.areasResumo.flatMap((area) =>
        area.resultadosDestaque.map((resultado) => ({
          Area: area.nome,
          ResultadoChave: resultado.nome,
          Progresso: resultado.valor,
        }))
      )), "KRs");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.projetosPrioritarios.map((projeto, index) => ({
        Posicao: index + 1,
        Projeto: projeto.titulo,
        Execução: projeto.execucao,
        Situação: this.formatarSituacaoProjeto(projeto.situacao),
        Saúde: this.formatarSaude(projeto.nivelSaude),
        Prioridade: projeto.prioridade,
        Prazo: projeto.prazoTexto,
      }))), "Prioritarios");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.projetosComIndicadores.map((projeto) => ({
        PDM: projeto.nomePdm,
        Projeto: projeto.titulo,
        Execução: projeto.execucao,
        Situação: this.formatarSituacaoProjeto(projeto.situacao),
        Saúde: this.formatarSaude(projeto.nivelSaude),
        Prioridade: projeto.prioridade,
        Prazo: projeto.prazoTexto,
      }))), "Projetos");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.projetosComIndicadores.flatMap((projeto) =>
        this.acoesDestaqueProjeto(projeto).map((acao) => ({
          Projeto: projeto.titulo,
          Acao: acao.nome,
          Status: acao.status,
          Prazo: acao.prazo,
          Progresso: acao.valor,
        }))
      )), "Acoes");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(this.projetosComIndicadores.flatMap((projeto) =>
        this.metricasDestaqueProjeto(projeto).map((metrica) => ({
          Projeto: projeto.titulo,
          KPI: metrica.nome,
          Progresso: metrica.valor,
        }))
      )), "KPIs");
      this.formatarPercentuaisXls(wb);
      XLSX.writeFile(wb, this.nomeArquivo("book-pdm-dados", "xlsx"));
    },
    formatarPercentuaisXls(wb) {
      (wb.SheetNames || []).forEach((nomeAba) => {
        this.formatarPercentuaisPlanilhaXls(wb.Sheets[nomeAba]);
      });
    },
    formatarPercentuaisPlanilhaXls(ws) {
      if (!ws?.["!ref"]) return;
      const intervalo = XLSX.utils.decode_range(ws["!ref"]);
      const cabecalhos = {};
      for (let coluna = intervalo.s.c; coluna <= intervalo.e.c; coluna += 1) {
        const ref = XLSX.utils.encode_cell({ r: intervalo.s.r, c: coluna });
        cabecalhos[coluna] = this.normalizarCabecalhoXls(ws[ref]?.v);
      }
      for (let linha = intervalo.s.r + 1; linha <= intervalo.e.r; linha += 1) {
        for (let coluna = intervalo.s.c; coluna <= intervalo.e.c; coluna += 1) {
          const ref = XLSX.utils.encode_cell({ r: linha, c: coluna });
          const celula = ws[ref];
          if (!celula) continue;
          const colunaPercentual = this.ehCabecalhoPercentualXls(cabecalhos[coluna]);
          const valorTextualPercentual = typeof celula.v === "string" && /^\s*-?\d+(?:[,.]\d+)?\s*%\s*$/.test(celula.v);
          if (colunaPercentual || valorTextualPercentual) {
            this.converterCelulaPercentualXls(celula, colunaPercentual);
          }
        }
      }
    },
    normalizarCabecalhoXls(valor) {
      return String(valor || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
    },
    ehCabecalhoPercentualXls(cabecalho) {
      const texto = String(cabecalho || "");
      const termos = ["execucao", "evolucao", "progresso", "percentual", "atingimento", "atingido", "conclusao"];
      if (termos.includes(texto) || texto.includes("percentual")) return true;
      return ["execucao", "evolucao", "progresso", "atingimento", "atingido", "conclusao"]
        .some((termo) => texto.startsWith(termo));
    },
    converterCelulaPercentualXls(celula, forcarEscalaPercentual = false) {
      const valor = this.obterValorPercentualXls(celula.v, forcarEscalaPercentual);
      if (valor === null) return;
      celula.t = "n";
      celula.v = valor;
      celula.z = "0%";
      delete celula.w;
    },
    obterValorPercentualXls(valor, forcarEscalaPercentual = false) {
      if (valor === null || valor === undefined || valor === "") return null;
      if (typeof valor === "number") {
        if (!Number.isFinite(valor)) return null;
        return forcarEscalaPercentual ? valor / 100 : (Math.abs(valor) > 1 ? valor / 100 : valor);
      }
      const texto = String(valor).trim();
      const temSinalPercentual = texto.endsWith("%");
      const textoNumerico = texto.replace("%", "").trim();
      const textoNormalizado = textoNumerico.includes(",")
        ? textoNumerico.replace(/\./g, "").replace(",", ".")
        : textoNumerico;
      const numero = Number(textoNormalizado);
      if (!Number.isFinite(numero)) return null;
      return (forcarEscalaPercentual || temSinalPercentual) ? numero / 100 : numero;
    },
    async renderizarElementoCanvas(elemento) {
      this.exportando = true;
      await this.$nextTick();
      return html2canvas(elemento, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        ignoreElements: (node) => node.classList?.contains("no-export"),
      });
    },
    baixarCanvas(canvas, nome) {
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = nome;
      link.click();
    },
    baixarPdfCanvas(canvas, nome) {
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      this.adicionarCanvasAoPdf(pdf, canvas);
      pdf.save(nome);
    },
    adicionarCanvasAoPdf(pdf, canvas) {
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margem = 18;
      const maxWidth = pageWidth - margem * 2;
      const maxHeight = pageHeight - margem * 2;
      const ratio = Math.min(maxWidth / canvas.width, maxHeight / canvas.height);
      const width = canvas.width * ratio;
      const height = canvas.height * ratio;
      const x = (pageWidth - width) / 2;
      const y = (pageHeight - height) / 2;
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.88), "JPEG", x, y, width, height, undefined, "FAST");
    },
  },
};
</script>

<style scoped>
.book-publico {
  min-height: 100vh;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: #061529;
  color: #f8fafc;
}
.book-topbar,
.book-filtros,
.book-loading,
.book-vazio,
.book-erro {
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 12px;
  background: rgba(8, 23, 48, 0.94);
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.22);
}
.book-topbar {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px;
}
.book-topbar h1,
.slide-head h2,
.capa-overlay h2 {
  margin: 0;
  letter-spacing: 0;
}
.book-topbar h1 {
  font-size: 1.9rem;
}
.book-topbar p {
  margin: 6px 0 0;
  color: #cbd5e1;
}
.eyebrow {
  margin: 0 0 6px;
  color: #22d3ee;
  font-size: .78rem;
  font-weight: 800;
  text-transform: uppercase;
}
.book-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.btn,
.slide-action button,
.card-export-group button {
  border: 0;
  border-radius: 8px;
  background: #22d3ee;
  color: #031225;
  cursor: pointer;
  font-weight: 800;
}
.btn {
  min-height: 38px;
  padding: 0 14px;
}
.btn.secondary {
  background: #1d4ed8;
  color: #fff;
}
.btn.ghost {
  background: rgba(255, 255, 255, 0.12);
  color: #e2e8f0;
  border: 1px solid rgba(226, 232, 240, 0.22);
}
.btn:disabled {
  opacity: .55;
  cursor: not-allowed;
}
.book-filtros {
  padding: 14px;
  display: grid;
  grid-template-columns: repeat(6, minmax(150px, 1fr));
  gap: 12px;
  align-items: end;
}
.book-filtros label {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.book-filtros span {
  color: #cbd5e1;
  font-size: .8rem;
  font-weight: 700;
}
.book-filtros select,
.book-filtros input {
  min-height: 42px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  background: #fff;
  color: #0f172a;
  padding: 0 10px;
}
.filtro-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  grid-column: 1 / -1;
}
.filtro-info span {
  padding: 8px 10px;
  border-radius: 999px;
  background: rgba(34, 211, 238, .12);
  color: #a5f3fc;
}
.filtro-limpar {
  min-height: 32px;
  border: 1px solid rgba(165, 243, 252, .45);
  border-radius: 999px;
  background: rgba(255, 255, 255, .08);
  color: #e0f2fe;
  cursor: pointer;
  font-size: .78rem;
  font-weight: 800;
  padding: 0 12px;
}
.book-slide {
  position: relative;
  width: min(100%, 1280px);
  min-height: 720px;
  margin: 0 auto;
  padding: 34px;
  overflow: hidden;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 16px;
  background: radial-gradient(circle at 20% 0%, #123664 0, #071831 42%, #061225 100%);
  box-shadow: 0 20px 56px rgba(0, 0, 0, 0.32);
}
.book-slide::after {
  content: "";
  position: absolute;
  right: 0;
  bottom: 0;
  width: 44%;
  height: 10px;
  background: linear-gradient(90deg, #22d3ee, #16a085 45%, #f97316);
}
.slide-marca {
  position: absolute;
  top: 28px;
  right: 34px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #dbeafe;
  font-size: .82rem;
  font-weight: 900;
  text-transform: uppercase;
}
.slide-marca i {
  color: #22d3ee;
  font-size: 1.35rem;
}
.slide-side-label {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 58px;
  display: grid;
  place-items: center;
  background: linear-gradient(180deg, #0f2c66, #123664);
  color: rgba(255, 255, 255, .24);
  font-size: 3.2rem;
  font-weight: 900;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  letter-spacing: 0;
}
.slide-side-label + .slide-head,
.slide-side-label ~ .slide-head,
.slide-side-label ~ .executivo-grid,
.slide-side-label ~ .area-grid,
.slide-side-label ~ .projetos-prioritarios,
.slide-side-label ~ .timeline {
  margin-left: 42px;
}
.slide-action {
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 5;
  display: flex;
  gap: 6px;
}
.slide-action,
.card-export-group {
  opacity: 0;
  pointer-events: none;
  transform: translateY(-4px);
  transition: opacity .16s ease, transform .16s ease;
}
.book-slide:hover > .slide-action,
.book-slide:focus-within > .slide-action,
.area-card:hover .card-export-group,
.area-card:focus-within .card-export-group,
.projeto-card:hover .card-export-group,
.projeto-card:focus-within .card-export-group {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
}
.slide-action button,
.card-export-group button {
  padding: 7px 9px;
  font-size: .74rem;
}
.capa {
  background-size: cover;
  background-position: center;
}
.capa-overlay {
  width: min(520px, 58%);
  padding-top: 40px;
}
.capa-overlay h2 {
  font-size: 4.6rem;
  line-height: .95;
}
.capa-overlay strong {
  display: block;
  margin-top: 24px;
  color: #fff;
  font-size: 2rem;
  line-height: 1.1;
}
.capa-overlay p {
  color: #dbeafe;
  font-size: 1.1rem;
}
.capa-ilustracao {
  position: absolute;
  right: 64px;
  top: 158px;
  width: 308px;
  display: grid;
  grid-template-columns: repeat(2, 120px);
  gap: 18px;
  transform: rotate(-6deg);
}
.capa-ilustracao i {
  display: grid;
  place-items: center;
  height: 120px;
  border: 1px solid rgba(255, 255, 255, .18);
  border-radius: 18px;
  background: rgba(255, 255, 255, .12);
  color: #a5f3fc;
  font-size: 3.3rem;
  backdrop-filter: blur(5px);
}
.capa-faixa {
  position: absolute;
  left: 34px;
  right: 34px;
  bottom: 92px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
}
.capa-faixa article,
.metricas-grid article,
.saude-box article,
.area-card,
.projeto-card {
  border: 1px solid rgba(226, 232, 240, 0.18);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.95);
  color: #0f172a;
}
.capa-faixa article {
  padding: 15px;
  min-height: 104px;
}
.capa-faixa article i,
.metricas-grid article i {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  margin-bottom: 8px;
  border-radius: 10px;
  background: #e0f2fe;
  color: #0f2c66;
  font-size: 1.45rem;
}
.capa-faixa span,
.metricas-grid span,
.saude-box span {
  display: block;
  color: #475569;
  font-size: .78rem;
  font-weight: 800;
  text-transform: uppercase;
}
.capa-faixa strong,
.metricas-grid strong,
.saude-box strong {
  display: block;
  color: #0f2c66;
  font-size: 2.1rem;
  line-height: 1;
  margin-top: 5px;
}
.slide-footer {
  position: absolute;
  left: 34px;
  right: 34px;
  bottom: 28px;
  display: flex;
  justify-content: space-between;
  color: #cbd5e1;
}
.slide-head {
  display: grid;
  grid-template-columns: 1fr minmax(320px, .82fr);
  gap: 22px;
  align-items: start;
  margin-bottom: 24px;
}
.slide-head.compacto {
  margin-bottom: 18px;
}
.slide-head span {
  color: #22d3ee;
  font-size: .88rem;
  font-weight: 800;
  text-transform: uppercase;
}
.slide-head h2 {
  color: #fff;
  font-size: 3rem;
  line-height: 1;
}
.slide-head p {
  margin: 0;
  color: #dbeafe;
  font-size: 1rem;
  line-height: 1.45;
}
.executivo-grid {
  display: grid;
  grid-template-columns: 300px 1fr 1fr;
  gap: 14px;
}
.progresso-card,
.status-box,
.saude-box,
.acoes-status-box {
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  color: #0f172a;
  padding: 18px;
}
.progresso-card {
  grid-row: span 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.progresso-card > span {
  color: #0f2c66;
  font-weight: 800;
  text-transform: uppercase;
}
.anel,
.mini-anel {
  display: grid;
  place-items: center;
  border-radius: 50%;
}
.anel {
  width: 185px;
  height: 185px;
  margin: 20px 0 12px;
}
.anel::before,
.mini-anel::before {
  content: "";
  position: absolute;
}
.anel strong,
.mini-anel strong {
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fff;
  color: #0f2c66;
  position: relative;
  z-index: 1;
}
.anel strong {
  width: 142px;
  height: 142px;
  font-size: 3rem;
}
.progresso-card small {
  color: #0891b2;
  font-weight: 900;
  text-transform: uppercase;
}
.metricas-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.metricas-grid article,
.saude-box article {
  padding: 16px;
}
.metricas-grid small,
.saude-box small {
  color: #64748b;
  font-weight: 700;
}
.status-box h3,
.saude-box h3,
.acoes-status-box h3 {
  margin: 0 0 12px;
  color: #0f2c66;
}
.status-row {
  display: grid;
  grid-template-columns: 115px 1fr 36px;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.status-row span {
  color: #334155;
  font-size: .82rem;
}
.status-row div {
  height: 8px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}
.status-row i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: #22d3ee;
}
.status-row i.status-concluida,
.status-concluida {
  background: #dcfce7;
  color: #166534;
}
.status-row i.status-concluida {
  background: #16a34a;
}
.status-row i.status-execucao,
.status-execucao {
  background: #dbeafe;
  color: #1e40af;
}
.status-row i.status-execucao {
  background: #2563eb;
}
.status-row i.status-planejamento,
.status-planejamento {
  background: #fef3c7;
  color: #92400e;
}
.status-row i.status-planejamento {
  background: #d97706;
}
.status-row i.status-atrasada,
.status-atrasada {
  background: #fee2e2;
  color: #991b1b;
}
.status-row i.status-atrasada {
  background: #dc2626;
}
.area-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}
.area-card {
  position: relative;
  min-height: 292px;
  padding: 18px;
}
.card-export-group {
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 6px;
}
.area-head {
  display: grid;
  grid-template-columns: 42px 1fr;
  gap: 10px;
  align-items: start;
}
.area-head span {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  color: #fff;
  font-weight: 900;
}
.area-head span i {
  font-size: 1.35rem;
}
.area-head small {
  display: block;
  margin-bottom: 3px;
  color: #0891b2;
  font-size: .72rem;
  font-weight: 900;
  text-transform: uppercase;
}
.area-head h3 {
  margin: 0;
  color: #0f2c66;
  font-size: 1.15rem;
  line-height: 1.18;
}
.area-descricao {
  margin: 12px 0 0;
  color: #475569;
  font-size: .8rem;
  font-weight: 700;
  line-height: 1.35;
}
.area-body {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 16px;
  align-items: center;
  margin-top: 16px;
}
.mini-anel {
  width: 112px;
  height: 112px;
}
.mini-anel strong {
  width: 82px;
  height: 82px;
  font-size: 1.55rem;
}
.area-body ul {
  list-style: none;
  margin: 0;
  padding: 0;
  color: #334155;
}
.area-body li {
  margin-bottom: 8px;
}
.area-destaques {
  display: grid;
  gap: 8px;
  margin-top: 12px;
}
.area-destaques p {
  margin: 0;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
  color: #0f172a;
}
.area-destaques span {
  display: block;
  color: #0891b2;
  font-size: .75rem;
  font-weight: 900;
  text-transform: uppercase;
}
.area-mini-objetivos {
  display: grid;
  gap: 7px;
  margin-top: 12px;
}
.area-mini-objetivos div {
  position: relative;
  min-height: 22px;
  padding-right: 48px;
  border-radius: 999px;
  background: #eef2f7;
  overflow: hidden;
}
.area-mini-objetivos div i {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 4px;
  border-radius: 999px;
}
.area-mini-objetivos span {
  display: block;
  padding: 5px 9px;
  color: #334155;
  font-size: .72rem;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.area-mini-objetivos strong {
  position: absolute;
  top: 5px;
  right: 9px;
  color: #0f2c66;
  font-size: .72rem;
}
.area-detalhe {
  background: linear-gradient(110deg, #061225 0%, #08204a 56%, #063449 100%);
}
.area-detalhe::before,
.projeto-detalhe::before,
.projetos::before {
  content: "";
  position: absolute;
  top: 0;
  right: 34px;
  width: 170px;
  height: 100%;
  background:
    linear-gradient(90deg, rgba(255,255,255,.08) 1px, transparent 1px),
    linear-gradient(180deg, rgba(255,255,255,.08) 1px, transparent 1px);
  background-size: 26px 26px;
  opacity: .34;
  pointer-events: none;
}
.area-head-slide h2 {
  max-width: 760px;
  font-size: 2.55rem;
}
.area-detalhe-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 310px 1fr;
  gap: 18px;
  margin-left: 42px;
}
.area-narrativa,
.objetivos-painel,
.projeto-raio,
.projeto-dados {
  border: 1px solid rgba(226, 232, 240, .2);
  border-radius: 14px;
  background: rgba(255, 255, 255, .96);
  color: #0f172a;
}
.area-narrativa {
  padding: 22px;
}
.area-icon-large {
  display: grid;
  place-items: center;
  width: 72px;
  height: 72px;
  border-radius: 18px;
  background: var(--area-cor);
  color: #fff;
  font-size: 2.3rem;
}
.area-ring {
  width: 172px;
  height: 172px;
  margin: 26px auto 18px;
}
.area-ring strong {
  width: 130px;
  height: 130px;
  font-size: 2.65rem;
}
.area-narrativa h3,
.objetivos-lista h3,
.acoes-projeto h3 {
  margin: 0 0 8px;
  color: #0f2c66;
}
.area-narrativa p {
  margin: 0;
  color: #475569;
  line-height: 1.45;
}
.objetivos-painel {
  padding: 18px;
}
.objetivo-extremos {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}
.objetivo-extremos article,
.area-resultados article,
.projeto-kpis article {
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  background: #f8fafc;
}
.objetivo-extremos article {
  padding: 14px;
}
.objetivo-extremos i {
  color: var(--area-cor);
  font-size: 1.65rem;
}
.objetivo-extremos span,
.area-resultados span,
.projeto-kpis span {
  display: block;
  color: #64748b;
  font-size: .72rem;
  font-weight: 900;
  text-transform: uppercase;
}
.objetivo-extremos strong {
  display: block;
  margin-top: 8px;
  color: #0f2c66;
  font-size: 1rem;
  line-height: 1.25;
}
.objetivos-lista {
  display: grid;
  gap: 10px;
}
.objetivo-row,
.acao-row,
.metrica-row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 260px 52px;
  gap: 10px;
  align-items: center;
}
.acao-row {
  grid-template-columns: minmax(210px, 1fr) 92px 86px 190px 48px;
}
.metrica-row {
  grid-template-columns: minmax(220px, 1fr) 210px 48px;
}
.objetivo-row span,
.acao-row span,
.metrica-row span {
  color: #0f172a;
  font-weight: 800;
  line-height: 1.25;
}
.objetivo-row span small {
  display: block;
  margin-top: 4px;
  color: #64748b;
  font-size: .68rem;
  font-weight: 700;
  line-height: 1.25;
}
.objetivo-row div,
.acao-row div,
.metrica-row div {
  height: 10px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}
.objetivo-row div i,
.acao-row div i,
.metrica-row div i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--area-cor, #22d3ee), #22d3ee);
}
.objetivo-row strong,
.acao-row strong,
.metrica-row strong {
  color: #0f2c66;
  font-weight: 900;
  text-align: right;
}
.acao-row small {
  color: #64748b;
  font-size: .72rem;
  font-weight: 800;
  text-align: center;
}
.area-resultados {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin: 16px 0 0 42px;
}
.area-resultados article {
  padding: 12px;
}
.area-resultados i {
  color: var(--area-cor);
  font-size: 1.35rem;
}
.area-resultados strong {
  display: block;
  margin-top: 6px;
  color: #0f2c66;
  font-size: 1.45rem;
}
.vazio-inline {
  margin: 0;
  color: #64748b;
  font-weight: 800;
}
.projetos-prioritarios {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-left: 42px;
}
.projeto-card {
  position: relative;
  min-height: 104px;
  padding: 16px;
  display: grid;
  grid-template-columns: 42px 68px minmax(0, 1fr) 300px;
  gap: 12px;
  align-items: center;
  border-left: 8px solid #22d3ee;
}
.projeto-card.prioridade-critica {
  border-left-color: #ef4444;
}
.projeto-card.prioridade-alta {
  border-left-color: #f97316;
}
.projeto-card.prioridade-monitorada {
  border-left-color: #16a085;
}
.projeto-posicao,
.projeto-visual {
  display: grid;
  place-items: center;
}
.projeto-posicao {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #0f2c66;
  color: #fff;
  font-weight: 900;
}
.projeto-visual {
  width: 58px;
  height: 58px;
  border-radius: 16px;
  background: #e0f2fe;
  color: #0f2c66;
  font-size: 1.85rem;
}
.projeto-visual.grande {
  width: 92px;
  height: 92px;
  margin-bottom: 18px;
  background: #0f2c66;
  color: #fff;
  font-size: 3rem;
}
.projeto-card h3 {
  margin: 4px 0 0;
  color: #0f2c66;
  font-size: 1rem;
  line-height: 1.22;
}
.projeto-card span {
  color: #64748b;
  font-size: .74rem;
  font-weight: 800;
}
.projeto-info p {
  margin: 6px 0 0;
  color: #475569;
  font-size: .78rem;
  font-weight: 800;
  line-height: 1.28;
}
.projeto-meta {
  display: grid;
  grid-template-columns: 62px repeat(3, minmax(54px, 1fr));
  gap: 6px;
  align-items: center;
}
.projeto-meta b {
  width: 58px;
  height: 58px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #0f2c66;
  color: #fff;
  font-size: 1.15rem;
}
.projeto-meta b small {
  display: block;
  color: rgba(255, 255, 255, .76);
  font-size: .58rem;
  line-height: 1;
  text-transform: uppercase;
}
.projeto-meta span {
  padding: 5px 7px;
  border-radius: 999px;
  background: #e0f2fe;
  color: #0f2c66;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.projeto-meta .meta-chip {
  display: flex;
  min-height: 42px;
  flex-direction: column;
  justify-content: center;
  border-radius: 12px;
  white-space: normal;
}
.meta-chip small,
.projeto-tags small {
  display: block;
  color: rgba(15, 44, 102, .72);
  font-size: .58rem;
  line-height: 1;
  text-transform: uppercase;
}
.chip-prioridade-alta {
  background: #fee2e2 !important;
  color: #991b1b !important;
}
.chip-prioridade-media {
  background: #fef3c7 !important;
  color: #92400e !important;
}
.chip-prioridade-baixa,
.chip-saude-saudavel {
  background: #dcfce7 !important;
  color: #166534 !important;
}
.chip-saude-atencao {
  background: #ffedd5 !important;
  color: #9a3412 !important;
}
.chip-saude-critica {
  background: #fee2e2 !important;
  color: #991b1b !important;
}
.chip-neutro {
  background: #e2e8f0 !important;
  color: #334155 !important;
}
.projeto-meta span:last-child {
  grid-column: 2 / -1;
}
.projeto-detalhe {
  background: linear-gradient(110deg, #061225 0%, #08204a 50%, #0b3b50 100%);
}
.projeto-detalhe-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 18px;
  margin-left: 42px;
}
.projeto-raio {
  padding: 22px;
}
.projeto-ring {
  width: 166px;
  height: 166px;
  margin: 0 auto 18px;
}
.projeto-ring strong {
  width: 126px;
  height: 126px;
  font-size: 2.55rem;
}
.projeto-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.projeto-tags span {
  padding: 7px 9px;
  border-radius: 12px;
  background: #e0f2fe;
  color: #0f2c66;
  font-size: .76rem;
  font-weight: 900;
}
.projeto-contexto {
  margin: 14px 0 0;
  color: #475569;
  font-size: .82rem;
  font-weight: 700;
  line-height: 1.4;
}
.acoes-mini-resumo {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 7px;
  margin-top: 14px;
}
.acoes-mini-resumo span,
.timeline-resumo span,
.timeline em,
.acao-row em {
  border-radius: 999px;
  font-size: .72rem;
  font-style: normal;
  font-weight: 900;
  padding: 6px 8px;
  text-align: center;
}
.acoes-mini-resumo b,
.timeline-resumo b {
  display: inline-block;
  margin-right: 4px;
}
.projeto-dados {
  padding: 18px;
}
.projeto-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 18px;
}
.projeto-kpis article {
  padding: 12px;
}
.projeto-kpis i {
  color: #0891b2;
  font-size: 1.45rem;
}
.projeto-kpis strong {
  display: block;
  margin-top: 6px;
  color: #0f2c66;
  font-size: 1rem;
  line-height: 1.2;
}
.acoes-projeto {
  display: grid;
  gap: 10px;
}
.metricas-detalhe {
  display: grid;
  gap: 8px;
  margin-bottom: 18px;
  padding: 12px;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  background: #f8fafc;
}
.metricas-detalhe h3 {
  margin: 0 0 4px;
  color: #0f2c66;
}
.timeline {
  display: grid;
  gap: 10px;
  margin-left: 42px;
}
.timeline-resumo {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 14px 42px;
  position: relative;
  z-index: 1;
}
.timeline article {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 82px;
  gap: 14px;
  align-items: center;
  padding: 13px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, .96);
  color: #0f172a;
}
.timeline h3 {
  margin: 4px 0;
  color: #0f2c66;
}
.timeline p {
  margin: 0;
  color: #475569;
}
.timeline small {
  display: block;
  margin-top: 5px;
  color: #64748b;
  font-weight: 800;
}
.timeline span {
  color: #0891b2;
  font-weight: 900;
}
.timeline strong {
  display: grid;
  place-items: center;
  width: 74px;
  height: 74px;
  border-radius: 50%;
  background: #e0f2fe;
  color: #0f2c66;
  font-size: 1.3rem;
}
.timeline em {
  justify-self: center;
}
.book-loading,
.book-vazio,
.book-erro {
  padding: 22px;
}
.book-erro {
  border-color: rgba(248, 113, 113, .45);
  background: rgba(127, 29, 29, .35);
  color: #fecaca;
}
@media (max-width: 980px) {
  .book-topbar,
  .slide-head,
  .executivo-grid,
  .book-filtros,
  .projeto-card,
  .area-detalhe-grid,
  .projeto-detalhe-grid,
  .objetivo-extremos,
  .objetivo-row,
  .acao-row,
  .metrica-row,
  .timeline article {
    grid-template-columns: 1fr;
  }
  .book-topbar {
    flex-direction: column;
  }
  .area-grid,
  .projetos-prioritarios,
  .area-resultados,
  .projeto-kpis,
  .capa-faixa {
    grid-template-columns: 1fr;
  }
  .slide-side-label,
  .capa-ilustracao {
    display: none;
  }
  .slide-side-label + .slide-head,
  .slide-side-label ~ .slide-head,
  .slide-side-label ~ .executivo-grid,
  .slide-side-label ~ .area-grid,
  .slide-side-label ~ .projetos-prioritarios,
  .slide-side-label ~ .timeline,
  .area-detalhe-grid,
  .area-resultados,
  .projetos-prioritarios,
  .projeto-detalhe-grid,
  .timeline {
    margin-left: 0;
  }
  .timeline-resumo {
    margin-left: 0;
  }
  .book-slide {
    min-height: auto;
  }
  .capa-overlay {
    width: auto;
  }
  .capa-overlay h2 {
    font-size: 2.8rem;
  }
  .capa-faixa,
  .slide-footer {
    position: static;
    margin-top: 24px;
  }
}
</style>
